import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Seo } from "@/seo/Seo";
import { toast } from "sonner";
import {
  ArrowRight, Loader2, Save, Send, ChevronLeft, LogOut, ExternalLink, ShieldCheck, Star,
  Columns, FileText, Check, Plus, Trash2, BellRing, XCircle, CheckCircle2, Scale, Hash, Lock,
  Settings as SettingsIcon, Bell, ShieldAlert, KeyRound, AlertTriangle, Package, Calculator, RefreshCw,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  adminVerify, adminListReviews, adminGetReview, adminSaveReview, adminPublishReview,
  adminSendCustomerReview, adminFailedReviews, adminDownloadReviewPdf,
  adminGenerateReview, adminGetJob, adminActiveJobs, adminRetryOption, adminReviewJob,
  adminListProposals, adminGetProposal, adminSaveProposal, adminProposalAction,
  adminListProjects, adminGetProject, adminSaveProject, adminCreateMilestone, adminSaveMilestone,
  adminCreateDeliverable, adminSaveDeliverable, adminProjectMessage,
  adminLegalCenter, adminListPackages, adminGetPackage, adminCreatePackage,
  adminPresentPackage, adminInvalidatePackage,
  adminListDeals, adminGetDeal, adminSaveDeal, adminPreviewDeal, adminCreateAgreementPayment, adminCloseDeal, adminDpaOverride,
  adminGetSettings, adminChangePasscode, adminUpdateNotifications, adminListAlerts,
  adminListCatalog, adminCreateService, adminPatchService, adminToggleService, adminCatalogQuote, adminDetectUpsells,
  adminSetLineItems, adminReleaseDeposit, adminDepositView, adminRequestMilestone,
  adminGenerateDoc, adminApproveDoc, adminSendDoc, adminDownloadDoc,
  setAdminPass, getAdminPass, clearAdminPass,
} from "@/lib/api";
import GrowthCenter from "@/pages/admin/GrowthCenter";
import { AssemblyPanel, HandoffPanel } from "@/components/admin/AssemblyPanel";

const SOL_KEYS = [["minimal", "Minimal"], ["recommended", "Recommended"], ["gtm_ready", "GTM Ready"]];
const TIERS = [["starter", "Starter"], ["standard", "Standard"], ["mvp_pro", "MVP Pro"], ["custom", "Custom"]];
const money = (v, c = "usd") => v == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: (c || "usd").toUpperCase() }).format(v);

const field = "w-full bg-[#0b0b0c] border border-[#1f1f1f] text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/20 transition-colors";
const labelCls = "mono-label text-zinc-500 text-xs";
const arr2lines = (a) => Array.isArray(a) ? a.join("\n") : (a || "");
const lines2arr = (s) => Array.isArray(s) ? s.map((x) => String(x).trim()).filter(Boolean) : (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const phases2lines = (a) => Array.isArray(a) ? a.map((p) => `${p.name || ""} — ${p.focus || ""}`).join("\n") : "";
const lines2phases = (s) => Array.isArray(s) ? s.map((p) => ({ name: p.name || "", focus: p.focus || "" })) : lines2arr(s).map((l) => { const [n, ...f] = l.split("—"); return { name: (n || "").trim(), focus: f.join("—").trim() }; });

const statusChip = (st) => {
  const map = {
    published: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
    draft: "text-amber-300 border-amber-400/30 bg-amber-400/10",
    accepted: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
    ready_for_project: "text-cyan-300 border-cyan-400/40 bg-cyan-400/10",
    payment_complete: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
    cancelled: "text-red-300 border-red-400/30 bg-red-400/10",
    changes_requested: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  };
  return map[st] || "text-zinc-300 border-[#232323] bg-[#0a0a0a]";
};

function Gate({ onIn }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try { await adminVerify(code.trim()); setAdminPass(code.trim()); onIn(); }
    catch { toast.error("Invalid passcode."); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
      <Seo noindex path="/admin" title="Internal Console · AIROTECH" />
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mono-label text-cyan-400 mb-6"><ShieldCheck className="h-4 w-4" /> AIROTECH · INTERNAL CONSOLE</div>
        <h1 className="font-heading font-extrabold tracking-tighter text-3xl">Reviewer sign-in.</h1>
        <p className="mt-3 text-zinc-400 text-sm">Enter the internal passcode.</p>
        <div className="mt-8 space-y-2">
          <Label className={labelCls}>Passcode</Label>
          <Input data-testid="admin-passcode" type="password" value={code} onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()} className="bg-[#0b0b0c] border-[#1f1f1f] text-white h-12" placeholder="••••••••••" />
        </div>
        <button data-testid="admin-enter" onClick={submit} disabled={busy}
          className="mt-6 inline-flex items-center gap-2 bg-white text-black font-medium px-7 py-3.5 rounded-full mono-label hover:bg-cyan-400 transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Enter <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    </div>
  );
}

function Shell({ tab, setTab, onLogout, children }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Seo noindex path="/admin" title="Internal Console · AIROTECH" />
      <header className="border-b border-[#141414] sticky top-0 z-40 bg-black/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="mono-label text-cyan-400 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> AIROTECH CONSOLE</div>
            <nav className="flex gap-1">
              <button data-testid="tab-reviews" onClick={() => setTab("reviews")} className={`mono-label px-3 py-1.5 rounded-full transition-colors ${tab === "reviews" ? "bg-cyan-400/10 text-cyan-300" : "text-zinc-500 hover:text-white"}`}>Product Reviews</button>
              <button data-testid="tab-proposals" onClick={() => setTab("proposals")} className={`mono-label px-3 py-1.5 rounded-full transition-colors ${tab === "proposals" ? "bg-cyan-400/10 text-cyan-300" : "text-zinc-500 hover:text-white"}`}>Commercial Proposals</button>
              <button data-testid="tab-projects" onClick={() => setTab("projects")} className={`mono-label px-3 py-1.5 rounded-full transition-colors ${tab === "projects" ? "bg-cyan-400/10 text-cyan-300" : "text-zinc-500 hover:text-white"}`}>Projects</button>
              <button data-testid="tab-packages" onClick={() => setTab("packages")} className={`mono-label px-3 py-1.5 rounded-full transition-colors ${tab === "packages" ? "bg-cyan-400/10 text-cyan-300" : "text-zinc-500 hover:text-white"}`}>Agreement Packages</button>
              <button data-testid="tab-deals" onClick={() => setTab("deals")} className={`mono-label px-3 py-1.5 rounded-full transition-colors ${tab === "deals" ? "bg-cyan-400/10 text-cyan-300" : "text-zinc-500 hover:text-white"}`}>Deals</button>
              <button data-testid="tab-legal" onClick={() => setTab("legal")} className={`mono-label px-3 py-1.5 rounded-full transition-colors ${tab === "legal" ? "bg-cyan-400/10 text-cyan-300" : "text-zinc-500 hover:text-white"}`}>Legal Center</button>
              <button data-testid="tab-catalog" onClick={() => setTab("catalog")} className={`mono-label px-3 py-1.5 rounded-full transition-colors ${tab === "catalog" ? "bg-cyan-400/10 text-cyan-300" : "text-zinc-500 hover:text-white"}`}>Catalog</button>
              <button data-testid="tab-growth" onClick={() => setTab("growth")} className={`mono-label px-3 py-1.5 rounded-full transition-colors ${tab === "growth" ? "bg-cyan-400/10 text-cyan-300" : "text-zinc-500 hover:text-white"}`}>Growth</button>
              <button data-testid="tab-alerts" onClick={() => setTab("alerts")} className={`mono-label px-3 py-1.5 rounded-full transition-colors ${tab === "alerts" ? "bg-cyan-400/10 text-cyan-300" : "text-zinc-500 hover:text-white"}`}>Alerts</button>
              <button data-testid="tab-settings" onClick={() => setTab("settings")} className={`mono-label px-3 py-1.5 rounded-full transition-colors ${tab === "settings" ? "bg-cyan-400/10 text-cyan-300" : "text-zinc-500 hover:text-white"}`}>Settings</button>
            </nav>
          </div>
          <button data-testid="admin-logout" onClick={onLogout} className="mono-label text-zinc-400 hover:text-white inline-flex items-center gap-1.5"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
        </div>
      </header>
      {children}
    </div>
  );
}

/* ---------------- Product Review console ---------------- */
const JOB_ACTIVE = ["queued", "generating_option_1", "generating_option_2", "generating_option_3"];

function GenerationStatus({ job, onRetryOption }) {
  const active = JOB_ACTIVE.includes(job.status);
  const done = job.status === "completed";
  const failed = job.status === "failed";
  const msg = job.progress?.message || (done ? "Your 3 proposals are ready." : "Working…");
  return (
    <div data-testid={`gen-status-${job.assessment_id}`}
      className="mt-3 rounded-lg border border-cyan-400/20 bg-cyan-400/[0.04] p-3">
      <div className="flex items-center gap-2">
        {active && <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-300" />}
        {done && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
        {failed && <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />}
        <span data-testid={`gen-msg-${job.assessment_id}`}
          className={`text-xs ${done ? "text-emerald-300" : failed ? "text-amber-300" : "text-cyan-200"}`}>{msg}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {["1", "2", "3"].map((n) => {
          const o = job.options?.[n] || {};
          return (
            <span key={n} data-testid={`gen-opt-${job.assessment_id}-${n}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 mono-label text-[11px] border ${o.state === "done" ? "border-emerald-400/30 text-emerald-300" : o.state === "error" ? "border-red-400/30 text-red-300" : "border-[#2a2a2a] text-zinc-400"}`}>
              {o.state === "done" ? <Check className="h-3 w-3" /> : o.state === "error" ? <XCircle className="h-3 w-3" /> : <Loader2 className={`h-3 w-3 ${active ? "animate-spin" : ""}`} />}
              {o.label || `Option ${n}`}
              {o.state === "error" && (
                <button data-testid={`retry-opt-${job.assessment_id}-${n}`} onClick={() => onRetryOption(job.job_id, Number(n))}
                  className="ml-1 underline hover:text-white">retry</button>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function ReviewList({ onOpen, onLogout }) {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState([]);
  const [retrying, setRetrying] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const [hiddenTest, setHiddenTest] = useState(0);
  const [jobs, setJobs] = useState({});
  const jobsRef = useRef({});
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);

  const loadFailed = async () => { try { const l = await adminFailedReviews(); setFailed(l); return l; } catch { return []; } };
  const loadReviews = async (incl) => { const d = await adminListReviews(incl); setRows(d.reviews); setHiddenTest(d.hidden_test_count || 0); };
  // Recover in-flight AND partially-failed jobs so a returning operator sees live
  // progress or can retry a failed option (requirement: recover on return).
  const recoverJobs = async (failedList) => {
    try {
      const active = await adminActiveJobs();
      const m = {}; (active || []).forEach((j) => { m[j.assessment_id] = j; });
      for (const f of (failedList || [])) {
        if (m[f.assessment_id]) continue;
        try {
          const j = await adminReviewJob(f.assessment_id);
          if (j && j.job_id && (JOB_ACTIVE.includes(j.status) || j.status === "failed")) m[f.assessment_id] = j;
        } catch { /* skip */ }
      }
      setJobs((prev) => ({ ...m, ...prev }));
    } catch { /* non-critical */ }
  };
  useEffect(() => { (async () => {
    try { await loadReviews(showTest); const l = await loadFailed(); await recoverJobs(l); }
    catch (e) { if (e?.response?.status === 401) { clearAdminPass(); onLogout(); } else toast.error("Failed to load."); }
  })(); }, [showTest]);

  // Poll active jobs so the operator sees live progress and recovers on return.
  useEffect(() => {
    const t = setInterval(async () => {
      const active = Object.values(jobsRef.current).filter((j) => JOB_ACTIVE.includes(j.status));
      if (active.length === 0) return;
      for (const j of active) {
        try {
          const upd = await adminGetJob(j.job_id);
          if (upd.status === "completed") {
            toast.success("Your 3 proposals are ready.");
            setJobs((prev) => { const n = { ...prev }; delete n[upd.assessment_id]; return n; });
            await loadReviews(showTest); await loadFailed();
          } else if (upd.status === "failed") {
            toast.error("Generation finished with errors — retry the failed option.");
            setJobs((prev) => ({ ...prev, [upd.assessment_id]: upd }));
            await loadFailed();
          } else {
            setJobs((prev) => ({ ...prev, [upd.assessment_id]: upd }));
          }
        } catch { /* keep polling */ }
      }
    }, 2500);
    return () => clearInterval(t);
  }, [showTest]);

  const generate = async (aid) => {
    setRetrying(aid);
    try {
      const job = await adminGenerateReview(aid);
      setJobs((prev) => ({ ...prev, [aid]: job }));
      toast.success("Generation started.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start generation."); }
    finally { setRetrying(null); }
  };
  const retryOption = async (jobId, n) => {
    try { const job = await adminRetryOption(jobId, n); setJobs((prev) => ({ ...prev, [job.assessment_id]: job })); toast.success(`Retrying Option ${n}…`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Retry failed."); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-extrabold tracking-tighter text-3xl md:text-4xl">Product Reviews.</h1>
          <p className="mt-3 text-zinc-400 text-sm">Every prospect's assessment with their three build options. Review, edit, download and send.</p>
        </div>
        {(hiddenTest > 0 || showTest) && (
          <button data-testid="toggle-test-data" onClick={() => setShowTest((v) => !v)}
            className="mono-label text-xs rounded-full border border-[#232323] text-zinc-400 px-4 py-2 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors">
            {showTest ? "Hide test data" : `Show ${hiddenTest} test ${hiddenTest === 1 ? "entry" : "entries"}`}
          </button>
        )}
      </div>
      {failed.length > 0 && (
        <div className="mt-6 rounded-2xl border border-amber-400/30 bg-amber-400/[0.06] p-5" data-testid="failed-reviews">
          <div className="mono-label text-amber-300 mb-1">PROSPECTS AWAITING A REVIEW ({failed.length})</div>
          <p className="text-zinc-400 text-xs mb-3">These prospects completed the questionnaire but don't have a Product Review yet. Generate one to see their three build options.</p>
          <div className="space-y-2">
            {failed.map((f) => {
              const job = jobs[f.assessment_id];
              const running = job && JOB_ACTIVE.includes(job.status);
              return (
                <div key={f.assessment_id} className="rounded-lg border border-amber-400/15 px-4 py-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0"><div className="text-white text-sm truncate">{f.product_name}</div><div className="text-zinc-500 text-xs truncate">{f.client_name}{f.email ? ` · ${f.email}` : ""}</div></div>
                    {running ? (
                      <span className="shrink-0 inline-flex items-center gap-1.5 mono-label text-[11px] text-cyan-300"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</span>
                    ) : (
                      <button data-testid={`retry-review-${f.assessment_id}`} onClick={() => generate(f.assessment_id)} disabled={retrying === f.assessment_id || f.can_generate === false}
                        title={f.can_generate === false ? "No analysis on file — cannot auto-generate" : ""}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 text-amber-200 px-4 py-1.5 mono-label hover:bg-amber-400/10 transition-colors disabled:opacity-40">
                        {retrying === f.assessment_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} {job?.status === "failed" ? "Retry all" : "Generate review"}
                      </button>
                    )}
                  </div>
                  {job && <GenerationStatus job={job} onRetryOption={retryOption} />}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="mt-8 space-y-3" data-testid="admin-review-list">
        {rows === null && <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {rows?.length === 0 && <div className="text-zinc-500">No product reviews yet.</div>}
        {rows?.map((r) => (
          <button key={r.assessment_id} data-testid={`admin-row-${r.assessment_id}`} onClick={() => onOpen(r.assessment_id)}
            className="w-full text-left rounded-2xl border border-[#161616] bg-[#0a0a0a] hover:border-cyan-400/40 transition-colors p-5 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-white font-medium truncate">{r.product_name}</div>
              <div className="text-zinc-500 text-sm truncate">{r.client_name}{r.company ? ` · ${r.company}` : ""} · {r.email}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="mono-label text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-3 py-1">{r.recommended_build_level_label}</span>
              <span className={`mono-label rounded-full px-3 py-1 border ${statusChip(r.status)}`}>{r.status === "published" ? "PUBLISHED" : "DRAFT"}</span>
              <ArrowRight className="h-4 w-4 text-zinc-500" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Comparison({ rev }) {
  const rows = [
    ["Build level", (s) => s.build_level_label],
    ["Investment", (s) => s.investment_range],
    ["Summary", (s) => s.summary],
    ["Capabilities", (s) => Array.isArray(s.core_capabilities) ? s.core_capabilities.join(", ") : ""],
    ["Tradeoffs", (s) => s.tradeoffs],
  ];
  return (
    <div className="overflow-x-auto rounded-xl border border-[#1f1f1f]" data-testid="review-comparison-table">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[#1f1f1f]">
          <th className="text-left p-3 text-zinc-500 mono-label">Attribute</th>
          {SOL_KEYS.map(([k, l]) => <th key={k} className="text-left p-3 text-cyan-300 mono-label">{l}<div className="text-white font-normal">{(rev[k] || {}).solution_name}</div></th>)}
        </tr></thead>
        <tbody>
          {rows.map(([label, fn]) => (
            <tr key={label} className="border-b border-[#141414] align-top">
              <td className="p-3 text-zinc-400 mono-label whitespace-nowrap">{label}</td>
              {SOL_KEYS.map(([k]) => <td key={k} className="p-3 text-zinc-300">{fn(rev[k] || {}) || "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReviewEditor({ aid, onBack, onLogout }) {
  const [d, setD] = useState(null);
  const [rev, setRev] = useState(null);
  const [recKey, setRecKey] = useState("recommended");
  const [busy, setBusy] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [autoSend, setAutoSend] = useState(() => localStorage.getItem("airo_auto_send_review") === "1");
  useEffect(() => { localStorage.setItem("airo_auto_send_review", autoSend ? "1" : "0"); }, [autoSend]);

  useEffect(() => { (async () => {
    try { const data = await adminGetReview(aid); setD(data); setRev(data.review.review || {}); setRecKey(data.review.recommended_solution_key || "recommended"); }
    catch (e) {
      if (e?.response?.status === 401) { clearAdminPass(); onLogout(); }
      else if (e?.response?.status === 404) { toast.error("That review isn't ready yet. Opening Product Reviews."); onBack(); }
      else toast.error("Failed to load review.");
    }
  })(); }, [aid]);

  const setTop = (k, v) => setRev((p) => ({ ...p, [k]: v }));
  const setSol = (sk, k, v) => setRev((p) => ({ ...p, [sk]: { ...(p[sk] || {}), [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...rev };
      for (const [k] of SOL_KEYS) {
        const s = payload[k] || {};
        if (typeof s.core_capabilities === "string") payload[k] = { ...s, core_capabilities: lines2arr(s.core_capabilities) };
      }
      const res = await adminSaveReview(aid, payload, recKey);
      setRev(res.review); toast.success("Draft saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); } finally { setBusy(false); }
  };
  const publish = async () => {
    setBusy(true);
    try {
      await save();
      const res = await adminPublishReview(aid, autoSend);
      if (res.delivery === "full_review") {
        toast.success(res.email_status === "sent"
          ? `Approved — full Product Review sent to ${res.recipient}.`
          : `Approved, but the customer email ${res.email_status}. Try "Send Product Review".`);
      } else {
        toast.success(res.email_status === "sent" ? "Published — client emailed." : `Published (email ${res.email_status}).`);
      }
      onBack();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed."); } finally { setBusy(false); }
  };
  const sendCustomerReview = async () => {
    setBusy(true);
    try { const res = await adminSendCustomerReview(aid); toast.success(`Product Review sent to ${res.recipient}.`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed."); } finally { setBusy(false); }
  };

  if (!rev) return <div className="flex items-center justify-center py-40"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>;
  const capsVal = (s) => Array.isArray(s?.core_capabilities) ? s.core_capabilities.join("\n") : (s?.core_capabilities || "");
  const a = d?.analysis;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="admin-editor">
      <div className="flex items-center justify-between gap-4 mb-6">
        <button data-testid="admin-back" onClick={onBack} className="mono-label text-zinc-400 hover:text-white inline-flex items-center gap-1.5"><ChevronLeft className="h-4 w-4" /> All reviews</button>
        <div className="flex items-center gap-3">
          <a href={d?.client_review_url} target="_blank" rel="noreferrer" className="mono-label text-zinc-400 hover:text-cyan-300 inline-flex items-center gap-1.5"><ExternalLink className="h-3.5 w-3.5" /> Client view</a>
          <button data-testid="review-compare-toggle" onClick={() => setShowCompare((v) => !v)} className="mono-label text-zinc-300 inline-flex items-center gap-1.5 border border-[#232323] rounded-full px-4 py-2 hover:border-cyan-400/50 transition-colors"><Columns className="h-3.5 w-3.5" /> Compare</button>
          <button data-testid="admin-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 border border-[#232323] text-white rounded-full px-4 py-2 mono-label hover:border-cyan-400/50 transition-colors disabled:opacity-50"><Save className="h-3.5 w-3.5" /> Save draft</button>
          <button data-testid="admin-download-review-pdf" onClick={() => adminDownloadReviewPdf(aid)} className="inline-flex items-center gap-1.5 border border-[#232323] text-white rounded-full px-4 py-2 mono-label hover:border-cyan-400/50 transition-colors"><ExternalLink className="h-3.5 w-3.5" /> Download PDF</button>
          <button data-testid="admin-send-customer-review" onClick={sendCustomerReview} disabled={busy} className="inline-flex items-center gap-1.5 border border-cyan-400/40 text-cyan-300 rounded-full px-4 py-2 mono-label hover:bg-cyan-400/10 transition-colors disabled:opacity-50"><Send className="h-3.5 w-3.5" /> Send Product Review</button>
          <label data-testid="auto-send-toggle" title="When approving, automatically email the customer the full Product Review (3 options + branded PDF + scheduling link)"
            className="inline-flex items-center gap-2 mono-label text-xs text-zinc-400 border border-[#232323] rounded-full px-3 py-2 cursor-pointer hover:border-cyan-400/40 select-none">
            <input type="checkbox" data-testid="auto-send-checkbox" checked={autoSend} onChange={(e) => setAutoSend(e.target.checked)}
              className="accent-cyan-400 h-3.5 w-3.5" />
            Auto-send on approve
          </label>
          <button data-testid="admin-publish" onClick={publish} disabled={busy} className="inline-flex items-center gap-1.5 bg-cyan-400 text-black rounded-full px-4 py-2 mono-label font-medium hover:bg-cyan-300 transition-colors disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {autoSend ? "Approve & send review" : "Publish & send"}</button>
        </div>
      </div>

      <div className="mono-label text-cyan-400">{d?.lead?.product_name}</div>
      <div className="text-zinc-500 text-sm mt-1 mb-6">{d?.lead?.name}{d?.lead?.company ? ` · ${d.lead.company}` : ""} · {d?.lead?.email} · Status: {d?.review?.status}</div>

      {showCompare && <div className="mb-8"><div className="mono-label text-cyan-400 mb-3">SIDE-BY-SIDE COMPARISON</div><Comparison rev={rev} /></div>}

      {a && (
        <details className="rounded-2xl border border-[#161616] bg-[#080808] p-5 mb-8">
          <summary className="mono-label text-zinc-400 cursor-pointer">INTERNAL AI ANALYSIS (not shown to client)</summary>
          <div className="mt-4 text-sm text-zinc-400 space-y-2">
            <p><span className="text-zinc-300">Suggested tier:</span> {a.suggested_tier} · <span className="text-zinc-300">Complexity:</span> {a.complexity}</p>
            <p><span className="text-zinc-300">Rationale:</span> {a.tier_rationale}</p>
            <p><span className="text-zinc-300">Summary:</span> {a.product_summary}</p>
          </div>
        </details>
      )}

      <div className="space-y-4 mb-8">
        <div className="mono-label text-cyan-400">NARRATIVE (client-facing)</div>
        {[["executive_summary", "Executive summary"], ["what_we_heard", "What we heard"], ["opportunity", "The opportunity"], ["recommendation_rationale", "Recommendation rationale"], ["recommended_next_step", "Recommended next step"]].map(([k, lbl]) => (
          <div key={k} className="space-y-1.5"><Label className={labelCls}>{lbl}</Label>
            <textarea data-testid={`admin-field-${k}`} className={field} rows={k === "recommended_next_step" ? 2 : 3} value={rev[k] || ""} onChange={(e) => setTop(k, e.target.value)} /></div>
        ))}
      </div>

      <div className="mb-6"><div className="mono-label text-cyan-400 mb-3">RECOMMENDED SOLUTION</div>
        <div className="flex flex-wrap gap-2" data-testid="admin-recommended-picker">
          {SOL_KEYS.map(([k, lbl]) => (
            <button key={k} data-testid={`admin-rec-${k}`} onClick={() => setRecKey(k)} aria-pressed={recKey === k}
              className={`mono-label rounded-full px-4 py-2 border transition-colors inline-flex items-center gap-1.5 ${recKey === k ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-[#232323] text-zinc-400 hover:border-zinc-500"}`}>
              {recKey === k && <Star className="h-3 w-3" />}{lbl}
            </button>
          ))}
        </div>
      </div>

      {SOL_KEYS.map(([sk, lbl]) => {
        const s = rev[sk] || {};
        return (
          <div key={sk} data-testid={`admin-solution-${sk}`} className={`rounded-2xl border p-6 space-y-4 mb-5 ${recKey === sk ? "border-cyan-400/40 bg-cyan-400/[0.04]" : "border-[#161616] bg-[#0a0a0a]"}`}>
            <div className="mono-label text-white">{lbl.toUpperCase()} SOLUTION</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className={labelCls}>Solution name</Label><input className={field} value={s.solution_name || ""} onChange={(e) => setSol(sk, "solution_name", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className={labelCls}>Build level</Label>
                <select data-testid={`admin-buildlevel-${sk}`} className={field} value={s.build_level || "custom"} onChange={(e) => setSol(sk, "build_level", e.target.value)}>{TIERS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select>
              </div>
            </div>
            <div className="space-y-1.5"><Label className={labelCls}>Summary</Label><textarea className={field} rows={2} value={s.summary || ""} onChange={(e) => setSol(sk, "summary", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className={labelCls}>Core capabilities (one per line)</Label><textarea className={field} rows={5} value={capsVal(s)} onChange={(e) => setSol(sk, "core_capabilities", e.target.value)} /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className={labelCls}>Why this solution</Label><textarea className={field} rows={3} value={s.why_this_solution || ""} onChange={(e) => setSol(sk, "why_this_solution", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className={labelCls}>Tradeoffs</Label><textarea className={field} rows={3} value={s.tradeoffs || ""} onChange={(e) => setSol(sk, "tradeoffs", e.target.value)} /></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Commercial Proposal console ---------------- */
function ProposalList({ onOpen, onLogout }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { (async () => {
    try { setRows((await adminListProposals()).proposals); }
    catch (e) { if (e?.response?.status === 401) { clearAdminPass(); onLogout(); } else toast.error("Failed to load."); }
  })(); }, []);
  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <h1 className="font-heading font-extrabold tracking-tighter text-3xl md:text-4xl">Commercial Proposals.</h1>
      <p className="mt-3 text-zinc-400 text-sm">Set final scope, price and payment structure, then approve and send.</p>
      <div className="mt-8 space-y-3" data-testid="admin-proposal-list">
        {rows === null && <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {rows?.length === 0 && <div className="text-zinc-500">No commercial proposals yet. They appear when a client selects a solution.</div>}
        {rows?.map((r) => (
          <button key={r.id} data-testid={`admin-proposal-row-${r.id}`} onClick={() => onOpen(r.id)}
            className="w-full text-left rounded-2xl border border-[#161616] bg-[#0a0a0a] hover:border-cyan-400/40 transition-colors p-5 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-white font-medium truncate">{r.product_name}</div>
              <div className="text-zinc-500 text-sm truncate">{r.client_name}{r.company ? ` · ${r.company}` : ""} · {r.email}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="mono-label text-white">{money(r.final_price, r.currency)}</span>
              <span className="mono-label text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-3 py-1">{r.build_level_label}</span>
              <span className={`mono-label rounded-full px-3 py-1 border ${statusChip(r.status)}`}>{(r.status || "").replace(/_/g, " ").toUpperCase()}</span>
              <ArrowRight className="h-4 w-4 text-zinc-500" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ProposalEditor({ id, onBack, onLogout, onOpenDeal }) {
  const [d, setD] = useState(null);
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [pickService, setPickService] = useState("");
  const [dep, setDep] = useState(null);

  const load = async () => { const data = await adminGetProposal(id); setD(data); setP(data.proposal); return data; };
  const loadDep = async () => { try { setDep(await adminDepositView(id)); } catch { /* non-critical */ } };
  useEffect(() => { (async () => {
    try {
      await load();
      await loadDep();
      try { const c = await adminListCatalog(); setCatalog((c.services || []).filter((s) => s.active)); } catch { /* non-critical */ }
    } catch (e) { if (e?.response?.status === 401) { clearAdminPass(); onLogout(); } else toast.error("Failed to load proposal."); }
  })(); }, [id]);
  const releaseDeposit = async () => {
    setBusy(true);
    try { await adminReleaseDeposit(id); toast.success("Released for deposit — client notified."); await load(); await loadDep(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not release."); } finally { setBusy(false); }
  };
  const requestMilestone = async (n) => {
    try { await adminRequestMilestone(dep.project.id, n); toast.success(`Milestone ${n} payment requested — client emailed.`); await loadDep(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not request milestone."); }
  };

  // Catalog service line items: pick a service from the dropdown -> price auto-attached,
  // total (final_price) recomputed on the server.
  const servicePrice = (s) => (s.base_price != null ? s.base_price : (s.minimum_price != null ? s.minimum_price : null));
  const persistLines = async (items) => {
    setP((x) => ({ ...x, catalog_line_items: items }));
    try {
      const res = await adminSetLineItems(id, {
        catalog_line_items: items,
        pass_through_fees: p.pass_through_fees || [],
        recurring_services: p.recurring_services || [],
      });
      setP((x) => ({ ...x, catalog_line_items: items, final_price: res.final_price }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update line items."); }
  };
  const addService = async (serviceId) => {
    const s = catalog.find((x) => x.service_id === serviceId);
    if (!s) return;
    const price = servicePrice(s);
    const items = [...(p.catalog_line_items || []), {
      service_id: s.service_id, name: s.service_name, category: s.category,
      price: price == null ? 0 : price, price_suffix: s.price_suffix || null,
      needs_price: price == null,
    }];
    setPickService("");
    await persistLines(items);
    toast.success(`Added ${s.service_name}${price != null ? ` — ${money(price)}` : " (set a price)"}.`);
  };
  const setLinePrice = (i, v) => setP((x) => { const items = [...(x.catalog_line_items || [])]; items[i] = { ...items[i], price: v === "" ? "" : Number(v), needs_price: false }; return { ...x, catalog_line_items: items }; });
  const removeService = async (i) => { await persistLines((p.catalog_line_items || []).filter((_, j) => j !== i)); };


  const setScope = (k, v) => setP((x) => ({ ...x, scope: { ...(x.scope || {}), [k]: v } }));
  const setField = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const setStructure = (structure) => {
    const defaults = {
      full: [{ name: "Full payment", percentage: 100, due_condition: "On agreement" }],
      deposit_balance: [{ name: "Deposit", percentage: 50, due_condition: "On acceptance" }, { name: "Balance", percentage: 50, due_condition: "On completion" }],
      installments: [{ name: "Installment 1", percentage: 50, due_condition: "On acceptance" }, { name: "Installment 2", percentage: 50, due_condition: "On completion" }],
      milestone: [{ name: "Milestone 1", percentage: 50, due_condition: "On start" }, { name: "Milestone 2", percentage: 50, due_condition: "On delivery" }],
      custom: (p.payment_schedule?.items) || [{ name: "Payment 1", percentage: 100, due_condition: "" }],
    };
    setP((x) => ({ ...x, payment_schedule: { structure, items: defaults[structure] } }));
  };
  const setItem = (i, k, v) => setP((x) => { const items = [...(x.payment_schedule?.items || [])]; items[i] = { ...items[i], [k]: k === "percentage" ? (v === "" ? "" : Number(v)) : v }; return { ...x, payment_schedule: { ...(x.payment_schedule || { structure: "custom" }), items } }; });
  const addItem = () => setP((x) => ({ ...x, payment_schedule: { structure: x.payment_schedule?.structure || "custom", items: [...(x.payment_schedule?.items || []), { name: "Payment", percentage: 0, due_condition: "" }] } }));
  const delItem = (i) => setP((x) => ({ ...x, payment_schedule: { ...(x.payment_schedule || {}), items: (x.payment_schedule?.items || []).filter((_, j) => j !== i) } }));

  const buildPayload = () => ({
    scope: {
      ...p.scope,
      included_capabilities: lines2arr(p.scope?.included_capabilities),
      excluded_capabilities: lines2arr(p.scope?.excluded_capabilities),
      technology_assumptions: lines2arr(p.scope?.technology_assumptions),
      client_responsibilities: lines2arr(p.scope?.client_responsibilities),
      airotech_responsibilities: lines2arr(p.scope?.airotech_responsibilities),
      implementation_phases: lines2phases(p.scope?.implementation_phases),
    },
    final_price: p.final_price === "" || p.final_price == null ? null : Number(p.final_price),
    currency: p.currency || "usd",
    payment_schedule: p.payment_schedule,
    valid_until: p.valid_until || null,
    selected_solution: p.selected_solution,
    selected_build_level: p.selected_build_level,
    reminders_enabled: p.reminders_enabled,
    internal_notes: p.internal_notes || "",
  });

  const save = async () => {
    setBusy(true);
    try { const res = await adminSaveProposal(id, buildPayload()); setP(res.proposal); toast.success("Proposal saved."); }
    catch (e) { console.error(e); toast.error(e?.response?.data?.detail || e?.message || "Save failed."); } finally { setBusy(false); }
  };
  const act = async (action, saveFirst = false) => {
    setBusy(true);
    try {
      if (saveFirst) await adminSaveProposal(id, buildPayload());
      const res = await adminProposalAction(id, action);
      toast.success(res.email_status ? `${action} (email ${res.email_status})` : `${action} done`);
      await load();
    } catch (e) { console.error(e); toast.error(e?.response?.data?.detail || e?.message || `${action} failed.`); } finally { setBusy(false); }
  };
  const closeDeal = async () => {
    setBusy(true);
    try {
      const r = await adminCloseDeal(id);
      toast.success("Deal draft created from proposal.");
      if (onOpenDeal && r.deal_id) onOpenDeal(r.deal_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not close the deal."); }
    finally { setBusy(false); }
  };

  if (!p) return <div className="flex items-center justify-center py-40"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>;
  const locked = ["accepted", "payment_pending", "payment_in_progress", "payment_complete", "ready_for_project"].includes(p.status);
  const items = p.payment_schedule?.items || [];
  const pctTotal = items.reduce((s, it) => s + (Number(it.percentage) || 0), 0);
  const price = Number(p.final_price) || 0;
  const asStr = (v) => Array.isArray(v) ? v.join("\n") : (v || "");

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="admin-proposal-editor">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <button data-testid="proposal-back" onClick={onBack} className="mono-label text-zinc-400 hover:text-white inline-flex items-center gap-1.5"><ChevronLeft className="h-4 w-4" /> All proposals</button>
        <div className="flex items-center gap-2 flex-wrap">
          {d?.workspace_url && <a href={d.workspace_url} target="_blank" rel="noreferrer" className="mono-label text-zinc-400 hover:text-cyan-300 inline-flex items-center gap-1.5"><ExternalLink className="h-3.5 w-3.5" /> Client workspace</a>}
          {!locked && <button data-testid="proposal-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 border border-[#232323] text-white rounded-full px-4 py-2 mono-label hover:border-cyan-400/50 transition-colors disabled:opacity-50"><Save className="h-3.5 w-3.5" /> Save</button>}
          {["draft", "internal_review", "changes_requested"].includes(p.status) && <button data-testid="proposal-approve" onClick={() => act("approve", true)} disabled={busy} className="inline-flex items-center gap-1.5 border border-emerald-400/40 text-emerald-300 rounded-full px-4 py-2 mono-label hover:bg-emerald-400/10 transition-colors disabled:opacity-50"><CheckCircle2 className="h-3.5 w-3.5" /> Approve</button>}
          {["approved", "sent", "viewed", "changes_requested"].includes(p.status) && <button data-testid="proposal-send" onClick={() => act("send")} disabled={busy} className="inline-flex items-center gap-1.5 bg-cyan-400 text-black rounded-full px-4 py-2 mono-label font-medium hover:bg-cyan-300 transition-colors disabled:opacity-50"><Send className="h-3.5 w-3.5" /> {p.status === "approved" ? "Send" : "Resend"}</button>}
          {["sent", "viewed"].includes(p.status) && <button data-testid="proposal-remind" onClick={() => act("remind")} disabled={busy} className="inline-flex items-center gap-1.5 border border-[#232323] text-white rounded-full px-4 py-2 mono-label hover:border-cyan-400/50 transition-colors disabled:opacity-50"><BellRing className="h-3.5 w-3.5" /> Remind</button>}
          {["approved", "sent", "viewed", "changes_requested", "accepted"].includes(p.status) && p.final_price && <button data-testid="proposal-close-deal" onClick={closeDeal} disabled={busy} className="inline-flex items-center gap-1.5 bg-white text-black rounded-full px-4 py-2 mono-label font-medium hover:bg-cyan-400 transition-colors disabled:opacity-50"><FileText className="h-3.5 w-3.5" /> {p.linked_deal_id ? "Reopen Deal" : "Close the Deal"}</button>}
          {!["accepted", "payment_complete", "ready_for_project", "cancelled"].includes(p.status) && <button data-testid="proposal-cancel" onClick={() => act("cancel")} disabled={busy} className="inline-flex items-center gap-1.5 border border-red-400/30 text-red-300 rounded-full px-4 py-2 mono-label hover:bg-red-400/10 transition-colors disabled:opacity-50"><XCircle className="h-3.5 w-3.5" /> Cancel</button>}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="mono-label text-cyan-400">{d?.lead?.product_name}</div>
        <span className={`mono-label rounded-full px-3 py-1 border ${statusChip(p.status)}`} data-testid="proposal-status">{(p.status || "").replace(/_/g, " ").toUpperCase()}</span>
        {!p.final_price && <span className="mono-label text-amber-300 border border-amber-400/30 bg-amber-400/10 rounded-full px-3 py-1">PRICING REQUIRED</span>}
      </div>
      <div className="text-zinc-500 text-sm mt-1 mb-8">{d?.lead?.name}{d?.lead?.company ? ` · ${d.lead.company}` : ""} · {d?.lead?.email}</div>

      {locked && <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.04] p-4 mono-label text-emerald-300 mb-8">ACCEPTED — commercial terms locked. Create a new proposal to change terms.</div>}

      {/* Selected solution + price */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="space-y-1.5"><Label className={labelCls}>Selected solution</Label>
          <select data-testid="proposal-solution" disabled={locked} className={field} value={p.selected_solution} onChange={(e) => setField("selected_solution", e.target.value)}>{SOL_KEYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        <div className="space-y-1.5"><Label className={labelCls}>Build level</Label>
          <select data-testid="proposal-buildlevel" disabled={locked} className={field} value={p.selected_build_level} onChange={(e) => setField("selected_build_level", e.target.value)}>{TIERS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></div>
        <div className="space-y-1.5"><Label className={labelCls}>Final price (USD)</Label>
          <input data-testid="proposal-price" disabled={locked} type="number" min="0" step="0.01" className={field} value={p.final_price ?? ""} onChange={(e) => setField("final_price", e.target.value)} placeholder="e.g. 8500" /></div>
      </div>
      <p className="text-xs text-zinc-600 -mt-6 mb-6">Published range for {TIERS.find(([v]) => v === p.selected_build_level)?.[1]}: {d?.investment_range || "—"}. AI cannot set the final price — you decide it.</p>

      {/* Service line items — pick from the catalog, price auto-attached */}
      {!locked && (
        <div className="mb-8 rounded-xl border border-[#1f1f1f] bg-[#0b0b0c] p-5 space-y-4" data-testid="proposal-line-items">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="mono-label text-cyan-400">SERVICES &amp; OFFERINGS</div>
            <div className="flex items-center gap-2">
              <select data-testid="line-item-service-select" className={`${field} min-w-[260px]`} value={pickService}
                onChange={(e) => { const v = e.target.value; setPickService(v); if (v) addService(v); }}>
                <option value="">+ Add a service from the catalog…</option>
                {[...new Set(catalog.map((s) => s.category))].map((cat) => (
                  <optgroup key={cat} label={String(cat).replace(/_/g, " ")}>
                    {catalog.filter((s) => s.category === cat).map((s) => (
                      <option key={s.service_id} value={s.service_id}>
                        {s.service_name} — {servicePrice(s) != null ? money(servicePrice(s)) + (s.price_suffix ? ` ${s.price_suffix}` : "") : "Custom / Review"}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
          {(p.catalog_line_items || []).length === 0 ? (
            <p className="text-zinc-600 text-sm">No services added yet. Pick from the dropdown above — the price is attached automatically and the total updates for you.</p>
          ) : (
            <div className="space-y-2" data-testid="line-item-rows">
              {(p.catalog_line_items || []).map((li, i) => (
                <div key={i} data-testid={`line-item-${i}`} className="flex items-center gap-3 flex-wrap border border-[#161616] rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-white text-sm">{li.name}</div>
                    {li.category && <div className="mono-label text-[10px] text-zinc-600">{String(li.category).replace(/_/g, " ")}</div>}
                  </div>
                  <div className="flex items-center gap-1 text-zinc-400">
                    <span className="text-zinc-600">$</span>
                    <input data-testid={`line-item-price-${i}`} type="number" min="0" step="0.01"
                      className={`${field} w-32 ${li.needs_price ? "border-amber-400/50" : ""}`}
                      value={li.price ?? ""} onChange={(e) => setLinePrice(i, e.target.value)}
                      onBlur={() => persistLines(p.catalog_line_items)} placeholder="0.00" />
                  </div>
                  <button data-testid={`line-item-remove-${i}`} onClick={() => removeService(i)} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-[#161616]">
                <span className="mono-label text-zinc-500">CATALOG TOTAL</span>
                <span data-testid="line-item-total" className="text-white font-medium">{money((p.catalog_line_items || []).reduce((s, li) => s + (Number(li.price) || 0), 0))}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Deposit-first workflow: release for $400 deposit + project activation status */}
      <div className="mb-8 rounded-xl border border-cyan-400/20 bg-cyan-400/[0.04] p-5" data-testid="deposit-ops-panel">
        <div className="mono-label text-cyan-400 mb-3">DEPOSIT &amp; PROJECT</div>
        {dep?.project_activated ? (
          <div data-testid="project-activated">
            <div className="flex items-center gap-2 mono-label text-emerald-300"><CheckCircle2 className="h-4 w-4" /> PROJECT ACTIVATED · DEPOSIT PAID</div>
            <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
              <div><div className="mono-label text-zinc-600 text-[10px]">TOTAL PROJECT</div><div className="text-white">{money(dep.project.total_project_price)}</div></div>
              <div><div className="mono-label text-zinc-600 text-[10px]">REMAINING</div><div className="text-white">{money(dep.project.remaining_balance)}</div></div>
              <div><div className="mono-label text-zinc-600 text-[10px]">STATUS</div><div className="text-white">{String(dep.project.status || "").replace(/_/g, " ")}</div></div>
            </div>
            <div className="mt-4 space-y-2">
              {(dep.milestones || []).map((m) => (
                <div key={m.milestone_number} data-testid={`ops-milestone-${m.milestone_number}`} className="flex items-center justify-between gap-3 border border-[#161616] rounded-lg px-3 py-2">
                  <div className="text-sm text-zinc-300">{m.label} — {m.percentage}% · <span className="text-white">{money(m.amount)}</span></div>
                  {m.status === "paid" ? <span className="mono-label text-emerald-300 text-xs">PAID</span>
                    : m.status === "requested" ? <span className="mono-label text-cyan-300 text-xs">REQUESTED</span>
                    : <button data-testid={`request-milestone-${m.milestone_number}`} onClick={() => requestMilestone(m.milestone_number)} className="mono-label text-xs border border-cyan-400/40 text-cyan-300 rounded-full px-3 py-1 hover:bg-cyan-400/10">Request payment</button>}
                </div>
              ))}
            </div>
          </div>
        ) : dep?.deposit_flow?.released ? (
          <div>
            <div className="text-sm text-zinc-300">Released for payment. Awaiting the client's <b className="text-white">{money(dep.deposit_flow.deposit_amount)}</b> deposit.</div>
            <div className="mt-1 text-xs text-zinc-500">Total {money(dep.deposit_flow.total_project_price)} · Remaining {money(dep.deposit_flow.remaining_balance)} · Split 30/40/30</div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-zinc-400 mb-3">Once you've set the price (services above), release this build for the client's $400 deposit. They accept the agreement first, then pay to activate the project.</p>
            <button data-testid="release-deposit-btn" onClick={releaseDeposit} disabled={busy || !p.final_price}
              className="inline-flex items-center gap-1.5 bg-cyan-400 text-black rounded-full px-4 py-2 mono-label font-medium hover:bg-cyan-300 transition-colors disabled:opacity-40">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Release for $400 deposit
            </button>
            {!p.final_price && <p className="mt-2 text-xs text-amber-300">Add catalog services to set the project price first.</p>}
          </div>
        )}
      </div>


      {/* Payment structure */}
      <div className="mb-8 space-y-4">
        <div className="mono-label text-cyan-400">PAYMENT STRUCTURE</div>
        <select data-testid="proposal-payment-structure" disabled={locked} className={`${field} max-w-xs`} value={p.payment_schedule?.structure || ""} onChange={(e) => setStructure(e.target.value)}>
          <option value="">Select a structure…</option>
          <option value="full">100% upfront</option>
          <option value="deposit_balance">Deposit + balance</option>
          <option value="installments">Installments</option>
          <option value="milestone">Milestone-based</option>
          <option value="custom">Custom</option>
        </select>
        {items.length > 0 && (
          <div className="space-y-2" data-testid="proposal-payment-items">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-2 flex-wrap">
                <input disabled={locked} className={`${field} flex-1 min-w-[140px]`} value={it.name || ""} onChange={(e) => setItem(i, "name", e.target.value)} placeholder="Name" />
                <input disabled={locked} type="number" className={`${field} w-24`} value={it.percentage ?? ""} onChange={(e) => setItem(i, "percentage", e.target.value)} placeholder="%" />
                <input disabled={locked} className={`${field} flex-1 min-w-[140px]`} value={it.due_condition || ""} onChange={(e) => setItem(i, "due_condition", e.target.value)} placeholder="Due condition" />
                <span className="mono-label text-zinc-400 w-24 text-right">{money(price * (Number(it.percentage) || 0) / 100)}</span>
                {!locked && <button onClick={() => delItem(i)} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
            <div className="flex items-center justify-between pt-1">
              {!locked && <button onClick={addItem} className="mono-label text-cyan-300 inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Add item</button>}
              <span className={`mono-label ${pctTotal === 100 ? "text-emerald-300" : "text-amber-300"}`}>TOTAL {pctTotal}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Validity */}
      <div className="mb-8 grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label className={labelCls}>Proposal valid until</Label>
          <input data-testid="proposal-valid-until" disabled={locked} type="date" className={field} value={(p.valid_until || "").slice(0, 10)} onChange={(e) => setField("valid_until", e.target.value ? new Date(e.target.value).toISOString() : null)} /></div>
        <div className="space-y-1.5 flex items-end"><label className="inline-flex items-center gap-2 text-sm text-zinc-400"><input type="checkbox" disabled={locked} checked={!!p.reminders_enabled} onChange={(e) => setField("reminders_enabled", e.target.checked)} /> Reminders enabled</label></div>
      </div>

      {/* Scope */}
      <div className="mb-8 space-y-4">
        <div className="mono-label text-cyan-400">SCOPE (client-facing)</div>
        {[["executive_summary", "Executive summary", false], ["business_objective", "Business objective", false], ["scope_summary", "Scope summary", false]].map(([k, lbl]) => (
          <div key={k} className="space-y-1.5"><Label className={labelCls}>{lbl}</Label><textarea data-testid={`proposal-scope-${k}`} disabled={locked} className={field} rows={2} value={asStr(p.scope?.[k])} onChange={(e) => setScope(k, e.target.value)} /></div>
        ))}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className={labelCls}>Included (one per line)</Label><textarea data-testid="proposal-included" disabled={locked} className={field} rows={6} value={asStr(p.scope?.included_capabilities)} onChange={(e) => setScope("included_capabilities", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className={labelCls}>Not included / deferred (one per line)</Label><textarea data-testid="proposal-excluded" disabled={locked} className={field} rows={6} value={asStr(p.scope?.excluded_capabilities)} onChange={(e) => setScope("excluded_capabilities", e.target.value)} /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className={labelCls}>Technology assumptions</Label><textarea disabled={locked} className={field} rows={4} value={asStr(p.scope?.technology_assumptions)} onChange={(e) => setScope("technology_assumptions", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className={labelCls}>Implementation phases ("Name — Focus")</Label><textarea disabled={locked} className={field} rows={4} value={typeof p.scope?.implementation_phases === "string" ? p.scope.implementation_phases : phases2lines(p.scope?.implementation_phases)} onChange={(e) => setScope("implementation_phases", e.target.value)} /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className={labelCls}>Client responsibilities</Label><textarea disabled={locked} className={field} rows={4} value={asStr(p.scope?.client_responsibilities)} onChange={(e) => setScope("client_responsibilities", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className={labelCls}>AIROTECH responsibilities</Label><textarea disabled={locked} className={field} rows={4} value={asStr(p.scope?.airotech_responsibilities)} onChange={(e) => setScope("airotech_responsibilities", e.target.value)} /></div>
        </div>
        <div className="space-y-1.5"><Label className={labelCls}>Change / scope policy</Label><textarea disabled={locked} className={field} rows={2} value={asStr(p.scope?.change_scope_policy)} onChange={(e) => setScope("change_scope_policy", e.target.value)} /></div>
        <div className="space-y-1.5"><Label className={labelCls}>Next steps</Label><textarea disabled={locked} className={field} rows={2} value={asStr(p.scope?.next_steps)} onChange={(e) => setScope("next_steps", e.target.value)} /></div>
      </div>

      {/* Internal notes */}
      <div className="mb-8 space-y-1.5">
        <Label className={labelCls}>Internal notes (never shown to client)</Label>
        <textarea data-testid="proposal-internal-notes" disabled={locked} className={field} rows={3} value={p.internal_notes || ""} onChange={(e) => setField("internal_notes", e.target.value)} />
      </div>

      {/* Commercial documents & standalone roadmaps (generate from this proposal) */}
      <div className="mb-8">
        <div className="mono-label text-cyan-400 mb-3">DOCUMENTS &amp; ROADMAPS</div>
        <DealDocuments proposalId={id} />
      </div>

      {/* Deterministic legal assembly (MSDA/SOW + IP Assignment) */}
      <div className="mb-8">
        <AssemblyPanel proposalId={id} />
      </div>

      {/* Payments + acceptance + audit */}
      {d?.payments?.length > 0 && (
        <div className="mb-6"><div className="mono-label text-cyan-400 mb-2">PAYMENTS</div>
          {d.payments.map((pay) => <div key={pay.id} className="text-sm text-zinc-400 flex justify-between border-b border-[#141414] py-1.5"><span>{pay.purpose} · {pay.status}</span><span className="text-white">{money(pay.amount, pay.currency)}</span></div>)}
        </div>
      )}
      {d?.acceptance && <div className="mb-6 mono-label text-emerald-300">✓ AGREEMENT ACCEPTED v{d.acceptance.version} · signed "{d.acceptance.typed_signature}" · {new Date(d.acceptance.accepted_at).toLocaleString()}</div>}
      {d?.events?.length > 0 && (
        <details className="rounded-2xl border border-[#161616] bg-[#080808] p-5"><summary className="mono-label text-zinc-400 cursor-pointer">AUDIT TRAIL ({d.events.length})</summary>
          <div className="mt-4 space-y-1.5 text-xs text-zinc-500">{d.events.map((e) => <div key={e.id}><span className="text-zinc-300">{e.type}</span> · {e.actor} · {new Date(e.at).toLocaleString()}</div>)}</div>
        </details>
      )}
    </div>
  );
}

const PSTATUS = [["project_setup", "Project Setup"], ["active", "Active"], ["awaiting_client", "Awaiting Client"], ["on_hold", "On Hold"], ["in_review", "In Review"], ["ready_for_launch", "Ready for Launch"], ["launched", "Launched"], ["handoff", "Handoff"], ["completed", "Completed"], ["cancelled", "Cancelled"]];
const MSTATUS = [["not_started", "Not Started"], ["in_progress", "In Progress"], ["awaiting_client", "Awaiting Client"], ["in_review", "In Review"], ["approved", "Approved"], ["completed", "Completed"], ["blocked", "Blocked"]];
const DSTATUS = [["upcoming", "Upcoming"], ["in_progress", "In Progress"], ["ready_for_review", "Ready for Review"], ["approved", "Approved"], ["revision_requested", "Revision Requested"], ["completed", "Completed"]];

function ProjectList({ onOpen, onLogout }) {
  const [d, setD] = useState(null);
  useEffect(() => { (async () => {
    try { setD(await adminListProjects()); }
    catch (e) { if (e?.response?.status === 401) { clearAdminPass(); onLogout(); } else toast.error("Failed to load."); }
  })(); }, []);
  const aw = d?.awaiting;
  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <h1 className="font-heading font-extrabold tracking-tighter text-3xl md:text-4xl">Projects.</h1>
      <p className="mt-3 text-zinc-400 text-sm">Operational view — what needs your attention.</p>
      {aw && (
        <div className="mt-6 flex flex-wrap gap-3" data-testid="projects-awaiting">
          {[["approvals", "awaiting approval"], ["payments_due", "payments due"], ["ready_for_launch", "ready for launch"], ["awaiting_scheduling", "awaiting scheduling"]].map(([k, l]) => (
            <span key={k} className={`mono-label rounded-full px-3 py-1.5 border ${aw[k] ? "text-amber-300 border-amber-400/30 bg-amber-400/10" : "text-zinc-500 border-[#1f1f1f]"}`}>{aw[k]} {l}</span>
          ))}
        </div>
      )}
      <div className="mt-8 space-y-3" data-testid="admin-project-list">
        {d === null && <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {d?.projects?.length === 0 && <div className="text-zinc-500">No projects yet. They appear when an engagement reaches Ready for Project.</div>}
        {d?.projects?.map((r) => (
          <button key={r.id} data-testid={`admin-project-row-${r.id}`} onClick={() => onOpen(r.id)}
            className="w-full text-left rounded-2xl border border-[#161616] bg-[#0a0a0a] hover:border-cyan-400/40 transition-colors p-5 flex items-center justify-between gap-4">
            <div className="min-w-0"><div className="text-white font-medium truncate">{r.project_name}</div>
              <div className="text-zinc-500 text-sm truncate">{r.client_name}{r.company ? ` · ${r.company}` : ""} · {r.current_milestone || "no active milestone"}</div></div>
            <div className="flex items-center gap-3 shrink-0">
              {r.pending_approvals > 0 && <span className="mono-label text-amber-300 border border-amber-400/30 bg-amber-400/10 rounded-full px-2.5 py-1 text-xs">{r.pending_approvals} to approve</span>}
              {r.next_payment && <span className="mono-label text-white text-xs">{money(r.next_payment.amount)} due</span>}
              <span className="mono-label text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-3 py-1">{r.build_level_label}</span>
              <span className="mono-label rounded-full px-3 py-1 border text-zinc-300 border-[#232323]">{(r.status_label || "").toUpperCase()}</span>
              <ArrowRight className="h-4 w-4 text-zinc-500" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function MilestoneRow({ m, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(m);
  const [dName, setDName] = useState(""); const [dLink, setDLink] = useState("");
  const save = async (patch) => { setBusy(true); try { await adminSaveMilestone(m.id, patch); toast.success("Saved."); await onRefresh(); } catch (e) { console.error(e); toast.error(e?.response?.data?.detail || "Failed."); } finally { setBusy(false); } };
  const addDeliverable = async () => { if (!dName.trim()) return; setBusy(true); try { await adminCreateDeliverable(m.id, { name: dName, link: dLink, status: "ready_for_review" }); setDName(""); setDLink(""); toast.success("Deliverable added."); await onRefresh(); } catch (e) { toast.error("Failed."); } finally { setBusy(false); } };
  return (
    <div data-testid={`admin-milestone-${m.id}`} className="rounded-2xl border border-[#161616] bg-[#0a0a0a] p-5 space-y-3">
      <div className="text-white font-medium">{String((m.order ?? 0) + 1).padStart(2, "0")} · {m.name}</div>
      <div className="grid sm:grid-cols-4 gap-3">
        <select data-testid={`milestone-status-${m.id}`} className={field} value={local.status} onChange={(e) => { setLocal({ ...local, status: e.target.value }); save({ status: e.target.value }); }}>{MSTATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <input type="date" className={field} defaultValue={(m.target_date || "").slice(0, 10)} onBlur={(e) => save({ target_date: e.target.value || null })} />
        <label className="inline-flex items-center gap-2 text-sm text-zinc-400"><input type="checkbox" defaultChecked={m.requires_approval} onChange={(e) => save({ requires_approval: e.target.checked })} /> Needs approval</label>
        <div className="flex gap-2"><input data-testid={`milestone-payment-${m.id}`} type="number" placeholder="Payment $" className={field} defaultValue={m.payment?.amount ?? ""} onBlur={(e) => e.target.value && save({ payment_amount: Number(e.target.value) })} /></div>
      </div>
      <textarea className={field} rows={2} placeholder="Internal notes (private)" defaultValue={m.internal_notes} onBlur={(e) => save({ internal_notes: e.target.value })} />
      {(m.deliverables || []).map((dv) => (
        <div key={dv.id} className="flex items-center justify-between gap-3 border border-[#1c1c1c] rounded-lg px-3 py-2 text-sm">
          <span className="text-zinc-300">{dv.name} v{dv.version}</span>
          <select className={`${field} max-w-[180px]`} defaultValue={dv.status} onChange={(e) => adminSaveDeliverable(dv.id, { status: e.target.value }).then(onRefresh)}>{DSTATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </div>
      ))}
      <div className="flex gap-2">
        <input data-testid={`add-deliverable-name-${m.id}`} className={field} placeholder="Deliverable name" value={dName} onChange={(e) => setDName(e.target.value)} />
        <input className={field} placeholder="Link (https://…)" value={dLink} onChange={(e) => setDLink(e.target.value)} />
        <button data-testid={`add-deliverable-${m.id}`} onClick={addDeliverable} disabled={busy} className="mono-label border border-[#232323] text-white rounded-full px-4 py-2 hover:border-cyan-400/50 transition-colors shrink-0"><Plus className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function ProjectEditor({ id, onBack, onLogout }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msName, setMsName] = useState("");
  const [msg, setMsg] = useState("");
  const load = async () => { const data = await adminGetProject(id); setD(data); return data; };
  useEffect(() => { (async () => { try { await load(); } catch (e) { if (e?.response?.status === 401) { clearAdminPass(); onLogout(); } else toast.error("Failed to load."); } })(); }, [id]);
  const p = d?.project;
  const saveProject = async (patch) => { setBusy(true); try { await adminSaveProject(id, patch); toast.success("Project updated."); await load(); } catch (e) { console.error(e); toast.error(e?.response?.data?.detail || "Failed."); } finally { setBusy(false); } };
  const addMilestone = async () => { if (!msName.trim()) return; setBusy(true); try { await adminCreateMilestone(id, { name: msName }); setMsName(""); await load(); } catch (e) { toast.error("Failed."); } finally { setBusy(false); } };
  const sendMsg = async () => { if (!msg.trim()) return; setBusy(true); try { await adminProjectMessage(id, msg.trim()); setMsg(""); await load(); } catch (e) { toast.error("Failed."); } finally { setBusy(false); } };
  if (!p) return <div className="flex items-center justify-center py-40"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>;
  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-8" data-testid="admin-project-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button data-testid="project-back" onClick={onBack} className="mono-label text-zinc-400 hover:text-white inline-flex items-center gap-1.5"><ChevronLeft className="h-4 w-4" /> All projects</button>
        {d.workspace_url && <a href={d.workspace_url} target="_blank" rel="noreferrer" className="mono-label text-zinc-400 hover:text-cyan-300 inline-flex items-center gap-1.5"><ExternalLink className="h-3.5 w-3.5" /> Client workspace</a>}
      </div>
      <div><div className="mono-label text-cyan-400">{p.project_name}</div><div className="text-zinc-500 text-sm mt-1">{p.client_name}{p.company ? ` · ${p.company}` : ""}</div></div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label className={labelCls}>Project status</Label>
          <select data-testid="project-status-select" className={field} value={p.status} onChange={(e) => saveProject({ status: e.target.value })}>{PSTATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <div className="space-y-1.5"><Label className={labelCls}>Launch date</Label>
          <input type="date" className={field} defaultValue={(p.launch_date || "").slice(0, 10)} onBlur={(e) => saveProject({ launch_date: e.target.value || null })} /></div>
      </div>
      <div className="space-y-1.5"><Label className={labelCls}>Internal notes (private)</Label>
        <textarea data-testid="project-internal-notes" className={field} rows={2} defaultValue={p.internal_notes} onBlur={(e) => saveProject({ internal_notes: e.target.value })} /></div>

      <div className="space-y-4">
        <div className="mono-label text-cyan-400">MILESTONES</div>
        {d.milestones.map((m) => <MilestoneRow key={m.id} m={m} onRefresh={load} />)}
        <div className="flex gap-2"><input data-testid="add-milestone-name" className={field} placeholder="New milestone name" value={msName} onChange={(e) => setMsName(e.target.value)} />
          <button data-testid="add-milestone" onClick={addMilestone} disabled={busy} className="mono-label bg-white text-black rounded-full px-5 py-2.5 hover:bg-cyan-400 transition-colors shrink-0 inline-flex items-center gap-1.5"><Plus className="h-4 w-4" /> Add</button></div>
      </div>

      {d.payments?.length > 0 && (
        <div><div className="mono-label text-cyan-400 mb-2">PAYMENTS</div>
          {d.payments.map((pp) => <div key={pp.id} className="text-sm text-zinc-400 flex justify-between border-b border-[#141414] py-1.5"><span>{pp.purpose} · {pp.pay_state}</span><span className="text-white">{money(pp.amount)}</span></div>)}</div>
      )}

      <HandoffPanel projectId={id} />

      <div className="space-y-3"><div className="mono-label text-cyan-400">PROJECT MESSAGES</div>
        <div className="space-y-2 max-h-56 overflow-y-auto">{d.messages.map((m) => <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${m.from_role === "client" ? "bg-cyan-400/[0.06] border border-cyan-400/20" : "bg-[#0f0f0f] border border-[#1c1c1c]"}`}><div className="mono-label text-zinc-500 text-xs mb-0.5">{m.from_role === "client" ? "CLIENT" : "AIROTECH"}</div>{m.body}</div>)}</div>
        <div className="flex gap-2"><input data-testid="project-message-input" className={field} placeholder="Message to client…" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} />
          <button data-testid="project-message-send" onClick={sendMsg} disabled={busy} className="mono-label bg-white text-black rounded-full px-5 py-2.5 hover:bg-cyan-400 transition-colors shrink-0">Send</button></div>
      </div>

      {d.events?.length > 0 && (
        <details className="rounded-2xl border border-[#161616] bg-[#080808] p-5"><summary className="mono-label text-zinc-400 cursor-pointer">AUDIT TRAIL ({d.events.length})</summary>
          <div className="mt-4 space-y-1.5 text-xs text-zinc-500">{d.events.map((e) => <div key={e.id}><span className="text-zinc-300">{e.type}</span> · {e.actor} · {new Date(e.at).toLocaleString()}</div>)}</div></details>
      )}
    </div>
  );
}

/* ---------------- Legal Center (Phase 2) ---------------- */
const audienceChip = (a) => a === "INTERNAL_ONLY"
  ? "text-red-300 border-red-400/30 bg-red-400/10"
  : "text-cyan-300 border-cyan-400/30 bg-cyan-400/10";

function LegalCenter() {
  const [docs, setDocs] = useState(null);
  useEffect(() => { (async () => {
    try { setDocs(await adminLegalCenter()); } catch { toast.error("Could not load Legal Center."); setDocs([]); }
  })(); }, []);
  if (!docs) return <div className="max-w-5xl mx-auto px-6 py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>;
  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="legal-center">
      <div className="flex items-center gap-2 mono-label text-cyan-400 mb-2"><Scale className="h-4 w-4" /> LEGAL CENTER</div>
      <h1 className="font-heading font-extrabold tracking-tighter text-3xl">Canonical legal documents.</h1>
      <p className="mt-3 text-zinc-400 text-sm">Every published version with its immutable SHA-256 hash, audience, and audit history.</p>
      <div className="mt-8 space-y-3">
        {docs.map((d) => (
          <div key={d.document_type} data-testid={`legal-doc-${d.document_type}`} className="border border-[#1f1f1f] rounded-2xl bg-[#0b0b0c] p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-white font-medium flex items-center gap-2">
                  <span className="text-zinc-500 mono-label">DOC {d.document_number}</span> {d.title}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className={`mono-label text-[10px] px-2 py-0.5 rounded-full border ${audienceChip(d.audience)}`} data-testid={`legal-audience-${d.document_type}`}>{d.audience}</span>
                  {d.requires_signature && <span className="mono-label text-[10px] px-2 py-0.5 rounded-full border text-amber-300 border-amber-400/30 bg-amber-400/10">SIGNATURE</span>}
                  {d.discoverable && <span className="mono-label text-[10px] px-2 py-0.5 rounded-full border text-zinc-300 border-[#232323]">PUBLIC /legal</span>}
                </div>
              </div>
              {d.current && (
                <div className="text-right">
                  <div className="mono-label text-emerald-300 text-xs">v{d.current.version} · {d.current.status}</div>
                  <div className="text-zinc-500 text-[11px] mt-1">EFF {d.current.effective_date || "—"}</div>
                </div>
              )}
            </div>
            {d.current && (
              <div className="mt-3 flex items-start gap-2 text-zinc-500 text-[10px] mono-label break-all">
                <Hash className="h-3 w-3 mt-0.5 shrink-0 text-cyan-400" /> {d.current.sha256_hash}
              </div>
            )}
            {d.superseded_versions.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#151515]">
                <div className="mono-label text-zinc-600 text-[10px] mb-1">SUPERSEDED</div>
                {d.superseded_versions.map((v) => (
                  <div key={v.document_id} className="text-zinc-500 text-[11px]">v{v.version} · {v.status} · {v.sha256_hash.slice(0, 24)}…</div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Agreement Packages (Phase 2) ---------------- */
function PackageList({ onOpen }) {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = async () => { try { setRows(await adminListPackages()); } catch { toast.error("Could not load packages."); setRows([]); } };
  useEffect(() => { load(); }, []);
  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="package-list">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mono-label text-cyan-400 mb-2"><FileText className="h-4 w-4" /> AGREEMENT PACKAGES</div>
          <h1 className="font-heading font-extrabold tracking-tighter text-3xl">Customer agreement packages.</h1>
        </div>
        <button data-testid="package-new-btn" onClick={() => setCreating((c) => !c)}
          className="inline-flex items-center gap-2 bg-white text-black font-medium px-5 py-2.5 rounded-full mono-label hover:bg-cyan-400 transition-colors">
          <Plus className="h-4 w-4" /> New Package
        </button>
      </div>
      {creating && <PackageCreate onDone={() => { setCreating(false); load(); }} />}
      <div className="mt-8 space-y-2">
        {rows == null ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>
          : rows.length === 0 ? <p className="text-zinc-500 text-sm">No packages yet.</p>
          : rows.map((p) => (
            <button key={p.id} data-testid={`package-row-${p.id}`} onClick={() => onOpen(p.id)}
              className="w-full text-left border border-[#1f1f1f] rounded-xl bg-[#0b0b0c] px-5 py-4 hover:border-cyan-400/40 transition-colors flex items-center justify-between gap-4">
              <div>
                <div className="text-white text-sm font-medium">{p.customer_name} <span className="text-zinc-500">· {p.engagement_id}</span></div>
                <div className="mono-label text-zinc-500 text-[11px] mt-1">{p.document_count} docs · {p.requires_signature ? "SIGNATURE REQUIRED" : "ACKNOWLEDGEMENT"} · {p.customer_email}</div>
              </div>
              <span className={`mono-label text-[10px] px-2.5 py-1 rounded-full border ${statusChip((p.status || "").toLowerCase())}`}>{p.status}</span>
            </button>
          ))}
      </div>
    </div>
  );
}

const ENG_TYPES = [["software_development", "Software Development"], ["ai_integration", "AI Integration"], ["consultation", "Consultation"], ["general", "General"]];

function PackageCreate({ onDone }) {
  const [f, setF] = useState({
    engagement_id: "", customer_id: "", customer_name: "", customer_email: "",
    engagement_type: "software_development", customer_type: "company",
    involves_software_development: true, involves_ip_assignment: true,
    involves_ai_providers: true, involves_data_processing: true, needs_nda: true,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.engagement_id || !f.customer_id || !f.customer_name || !/\S+@\S+\.\S+/.test(f.customer_email))
      return toast.error("Fill engagement, customer id, name and a valid email.");
    setBusy(true);
    try {
      const pkg = await adminCreatePackage(f);
      await adminPresentPackage(pkg.id);
      toast.success("Package created and presented.");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create package."); }
    finally { setBusy(false); }
  };
  const Check2 = ({ k, label }) => (
    <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
      <input data-testid={`pkg-${k}`} type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} className="h-4 w-4 accent-cyan-400" /> {label}
    </label>
  );
  return (
    <div className="mt-6 border border-[#1f1f1f] rounded-2xl bg-[#0b0b0c] p-5" data-testid="package-create">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label className={labelCls}>Engagement ID</Label><Input data-testid="pkg-engagement-id" className={field} value={f.engagement_id} onChange={(e) => set("engagement_id", e.target.value)} /></div>
        <div><Label className={labelCls}>Customer ID</Label><Input data-testid="pkg-customer-id" className={field} value={f.customer_id} onChange={(e) => set("customer_id", e.target.value)} /></div>
        <div><Label className={labelCls}>Customer Name</Label><Input data-testid="pkg-customer-name" className={field} value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></div>
        <div><Label className={labelCls}>Customer Email</Label><Input data-testid="pkg-customer-email" className={field} value={f.customer_email} onChange={(e) => set("customer_email", e.target.value)} /></div>
        <div>
          <Label className={labelCls}>Engagement Type</Label>
          <select data-testid="pkg-engagement-type" className={field} value={f.engagement_type} onChange={(e) => set("engagement_type", e.target.value)}>
            {ENG_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <Label className={labelCls}>Customer Type</Label>
          <select data-testid="pkg-customer-type" className={field} value={f.customer_type} onChange={(e) => set("customer_type", e.target.value)}>
            <option value="company">Company</option><option value="individual">Individual</option>
          </select>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
        <Check2 k="involves_software_development" label="Software development" />
        <Check2 k="involves_ip_assignment" label="IP assignment" />
        <Check2 k="involves_ai_providers" label="AI providers" />
        <Check2 k="involves_data_processing" label="Data processing" />
        <Check2 k="needs_nda" label="Needs NDA" />
      </div>
      <button data-testid="pkg-create-submit" onClick={submit} disabled={busy}
        className="mt-5 inline-flex items-center gap-2 bg-white text-black font-medium px-6 py-3 rounded-full mono-label hover:bg-cyan-400 transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> Create & Present</>}
      </button>
    </div>
  );
}

function PackageDetail({ id, onBack }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { setD(await adminGetPackage(id)); } catch { toast.error("Could not load package."); } };
  useEffect(() => { load(); }, [id]);
  if (!d) return <div className="max-w-4xl mx-auto px-6 py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>;
  const invalidate = async () => { setBusy(true); try { await adminInvalidatePackage(id); toast.success("Invalidated."); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); } finally { setBusy(false); } };
  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="package-detail">
      <button onClick={onBack} className="mono-label text-zinc-400 hover:text-white inline-flex items-center gap-1.5 mb-6"><ChevronLeft className="h-4 w-4" /> Back</button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-extrabold tracking-tighter text-2xl">{d.package_name}</h1>
          <div className="mono-label text-zinc-500 text-xs mt-2">{d.customer_name} · {d.customer_email} · {d.engagement_id}</div>
        </div>
        <span className={`mono-label text-[10px] px-2.5 py-1 rounded-full border ${statusChip((d.status || "").toLowerCase())}`} data-testid="package-status">{d.status}</span>
      </div>
      {d.customer_url && (
        <div className="mt-4 flex items-center gap-2 text-cyan-300 text-xs break-all">
          <ExternalLink className="h-3.5 w-3.5 shrink-0" /> <a href={d.customer_url} target="_blank" rel="noreferrer" data-testid="package-customer-url">{d.customer_url}</a>
        </div>
      )}
      <div className="mt-3 text-zinc-600 text-[10px] mono-label break-all">PACKAGE HASH {d.package_hash}</div>

      <div className="mt-6 space-y-2">
        {d.documents.map((e) => (
          <div key={e.document_id} className="border border-[#1f1f1f] rounded-xl bg-[#0b0b0c] px-4 py-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-white text-sm"><span className="text-zinc-500 mono-label">DOC {e.document_number}</span> {e.title} <span className="text-zinc-500">v{e.version}</span></div>
              {e.requires_signature ? <span className="mono-label text-[10px] text-amber-300">SIGNATURE</span> : <span className="mono-label text-[10px] text-emerald-300">ACK</span>}
            </div>
            <div className="mt-1 text-zinc-600 text-[10px] mono-label break-all">{e.sha256_hash}</div>
            <div className="mt-1 text-zinc-600 text-[11px]">{e.required_reason}</div>
          </div>
        ))}
      </div>

      {d.acceptance && (
        <div className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-5" data-testid="package-acceptance">
          <div className="mono-label text-emerald-300 text-xs mb-2">ACCEPTANCE RECORD (IMMUTABLE)</div>
          <div className="text-sm text-emerald-100">{d.acceptance.accepting_user_name} · {d.acceptance.accepting_user_email}</div>
          <div className="text-emerald-300/70 text-xs mt-1">At {d.acceptance.accepted_at} · IP {d.acceptance.ip_address || "—"}</div>
          <div className="text-emerald-300/70 text-[10px] mono-label mt-1 break-all">ID {d.acceptance.id}</div>
        </div>
      )}

      <div className="mt-6">
        <div className="mono-label text-zinc-600 text-[10px] mb-2">AUDIT TRAIL</div>
        <div className="space-y-1" data-testid="package-audit">
          {d.audit.map((ev) => (
            <div key={ev.id} className="text-zinc-500 text-[11px] flex items-center gap-2">
              <span className="text-cyan-400 mono-label">{ev.event}</span>
              <span className="text-zinc-600">{ev.at}</span>
              {ev.actor && <span className="text-zinc-600">· {ev.actor}</span>}
            </div>
          ))}
        </div>
      </div>

      {d.status !== "ACCEPTED" && d.status !== "INVALIDATED" && (
        <button data-testid="package-invalidate" onClick={invalidate} disabled={busy}
          className="mt-8 inline-flex items-center gap-2 border border-red-400/30 text-red-300 px-5 py-2.5 rounded-full mono-label hover:bg-red-400/10 transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><XCircle className="h-4 w-4" /> Invalidate</>}
        </button>
      )}
    </div>
  );
}

/* ---------------- Deals / Closing (Phase 3) ---------------- */
const PAY_STRUCTS = [["full", "Pay in full"], ["deposit_balance", "Deposit + balance"],
  ["installments", "Fixed installments"], ["custom", "Custom schedule"], ["recurring", "Recurring"]];

function DealList({ onOpen }) {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = async () => { try { setRows(await adminListDeals()); } catch { toast.error("Could not load deals."); setRows([]); } };
  useEffect(() => { load(); }, []);
  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="deal-list">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mono-label text-cyan-400 mb-2"><FileText className="h-4 w-4" /> DEALS</div>
          <h1 className="font-heading font-extrabold tracking-tighter text-3xl">Closing workflow.</h1>
        </div>
        <button data-testid="deal-new-btn" onClick={() => setCreating((c) => !c)}
          className="inline-flex items-center gap-2 bg-white text-black font-medium px-5 py-2.5 rounded-full mono-label hover:bg-cyan-400 transition-colors">
          <Plus className="h-4 w-4" /> New Deal
        </button>
      </div>
      {creating && <DealCreate onDone={(id) => { setCreating(false); load(); if (id) onOpen(id); }} />}
      <div className="mt-8 space-y-2">
        {rows == null ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>
          : rows.length === 0 ? <p className="text-zinc-500 text-sm">No deals yet.</p>
          : rows.map((p) => (
            <button key={p.id} data-testid={`deal-row-${p.id}`} onClick={() => onOpen(p.id)}
              className="w-full text-left border border-[#1f1f1f] rounded-xl bg-[#0b0b0c] px-5 py-4 hover:border-cyan-400/40 flex items-center justify-between gap-4">
              <div>
                <div className="text-white text-sm font-medium">{p.customer_name} <span className="text-zinc-500">· {p.service_name || p.engagement_id}</span></div>
                <div className="mono-label text-zinc-500 text-[11px] mt-1">{p.total_amount} {(p.currency || "").toUpperCase()} · {p.payment_structure}</div>
              </div>
              <span className={`mono-label text-[10px] px-2.5 py-1 rounded-full border ${statusChip((p.state || "").toLowerCase())}`}>{p.state}</span>
            </button>
          ))}
      </div>
    </div>
  );
}

function DealCreate({ onDone }) {
  const [f, setF] = useState({ engagement_id: "", customer_id: "", customer_name: "", customer_email: "",
    service_name: "", total_amount: "", currency: "usd", payment_structure: "full", deposit_amount: "",
    installment_amount: "", installment_count: "", recurring_amount: "", billing_interval: "month",
    start_date: "", scope: "", closing_sequence: "sign_then_pay" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.engagement_id || !f.customer_id || !f.customer_name || !/\S+@\S+\.\S+/.test(f.customer_email) || !f.service_name)
      return toast.error("Fill engagement, customer, email, service.");
    setBusy(true);
    try {
      const payload = { ...f,
        total_amount: f.total_amount ? Number(f.total_amount) : null,
        deposit_amount: f.deposit_amount ? Number(f.deposit_amount) : null,
        installment_amount: f.installment_amount ? Number(f.installment_amount) : null,
        installment_count: f.installment_count ? Number(f.installment_count) : null,
        recurring_amount: f.recurring_amount ? Number(f.recurring_amount) : null };
      const d = await adminSaveDeal(payload);
      toast.success("Deal draft saved."); onDone(d.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save deal."); }
    finally { setBusy(false); }
  };
  const F = ({ k, label, type = "text" }) => (
    <div><Label className={labelCls}>{label}</Label>
      <Input data-testid={`deal-${k}`} type={type} className={field} value={f[k]} onChange={(e) => set(k, e.target.value)} /></div>
  );
  return (
    <div className="mt-6 border border-[#1f1f1f] rounded-2xl bg-[#0b0b0c] p-5" data-testid="deal-create">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <F k="engagement_id" label="Engagement ID" /><F k="customer_id" label="Customer ID" />
        <F k="customer_name" label="Customer / Company" /><F k="customer_email" label="Customer Email" />
        <F k="service_name" label="Service / Project" /><F k="total_amount" label="Total Amount" type="number" />
        <div><Label className={labelCls}>Payment Structure</Label>
          <select data-testid="deal-payment_structure" className={field} value={f.payment_structure} onChange={(e) => set("payment_structure", e.target.value)}>
            {PAY_STRUCTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <F k="start_date" label="Start Date" />
        {f.payment_structure === "deposit_balance" && <F k="deposit_amount" label="Deposit Amount" type="number" />}
        {f.payment_structure === "installments" && <><F k="deposit_amount" label="Deposit (optional)" type="number" /><F k="installment_amount" label="Installment Amount" type="number" /><F k="installment_count" label="Installment Count" type="number" /></>}
        {f.payment_structure === "recurring" && <F k="recurring_amount" label="Recurring Amount" type="number" />}
        <div><Label className={labelCls}>Closing Sequence</Label>
          <select data-testid="deal-closing_sequence" className={field} value={f.closing_sequence} onChange={(e) => set("closing_sequence", e.target.value)}>
            <option value="sign_then_pay">Sign → Pay</option><option value="pay_then_sign">Pay → Sign</option></select></div>
      </div>
      <button data-testid="deal-save" onClick={submit} disabled={busy}
        className="mt-5 inline-flex items-center gap-2 bg-white text-black font-medium px-6 py-3 rounded-full mono-label hover:bg-cyan-400 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save Draft</>}
      </button>
    </div>
  );
}

function DealDetail({ id, onBack }) {
  const [d, setD] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { setD(await adminGetDeal(id)); } catch { toast.error("Could not load deal."); } };
  useEffect(() => { load(); }, [id]);
  if (!d) return <div className="max-w-4xl mx-auto px-6 py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>;
  const t = d.terms || {};
  const doPreview = async () => { setBusy(true); try { setPreview(await adminPreviewDeal(id)); } catch (e) { toast.error("Preview failed."); } finally { setBusy(false); } };
  const confirm = async () => {
    setBusy(true);
    try { const r = await adminCreateAgreementPayment(id); toast.success("Agreement created & sent."); setPreview(null); load(); }
    catch (e) { const dt = e?.response?.data?.detail; toast.error(dt?.missing ? `Missing: ${dt.missing.join(", ")}` : (dt?.message || dt || "Failed.")); }
    finally { setBusy(false); }
  };
  const doOverride = async (decision) => {
    let reason = "";
    if (decision !== "auto") { reason = window.prompt(`Reason for marking DPA ${decision === "required" ? "REQUIRED" : "NOT REQUIRED"}:`) || ""; }
    setBusy(true);
    try { await adminDpaOverride(id, decision, reason); toast.success("DPA determination updated."); setPreview(await adminPreviewDeal(id)); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not set override."); }
    finally { setBusy(false); }
  };
  const frozen = d.state !== "DRAFT" && d.state !== "READY_TO_SEND";
  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="deal-detail">
      <button onClick={onBack} className="mono-label text-zinc-400 hover:text-white inline-flex items-center gap-1.5 mb-6"><ChevronLeft className="h-4 w-4" /> Back</button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div><h1 className="font-heading font-extrabold tracking-tighter text-2xl">{t.service_name || d.engagement_id}</h1>
          <div className="mono-label text-zinc-500 text-xs mt-2">{t.customer_name} · {t.customer_email}</div></div>
        <span className={`mono-label text-[10px] px-2.5 py-1 rounded-full border ${statusChip((d.state || "").toLowerCase())}`} data-testid="deal-state">{d.state}</span>
      </div>
      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <div><span className="text-zinc-500 mono-label">TOTAL</span><div className="text-white text-lg" data-testid="deal-total">{t.total_amount} {(t.currency || "").toUpperCase()}</div></div>
        <div><span className="text-zinc-500 mono-label">STRUCTURE</span><div className="text-white">{t.payment_structure}</div></div>
        <div><span className="text-zinc-500 mono-label">START</span><div className="text-white">{t.start_date}</div></div>
      </div>
      {d.customer_url && <div className="mt-3 flex items-center gap-2 text-cyan-300 text-xs break-all"><ExternalLink className="h-3.5 w-3.5" /><a href={d.customer_url} target="_blank" rel="noreferrer" data-testid="deal-customer-url">{d.customer_url}</a></div>}
      {d.package && <div className="mt-2 text-zinc-600 text-[10px] mono-label break-all">PACKAGE HASH {d.package.package_hash}</div>}

      <DpaDetermination dp={preview?.data_processing} frozen={d.snapshot?.dpa_classification} disabled={busy || frozen} onOverride={doOverride} />

      {frozen && <DealDocuments dealId={id} />}

      {!frozen && (
        <div className="mt-8 border-t border-[#141414] pt-6">
          {!preview ? (
            <button data-testid="deal-preview" onClick={doPreview} disabled={busy}
              className="inline-flex items-center gap-2 border border-[#232323] text-white px-6 py-3 rounded-full mono-label hover:border-cyan-400/40 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Preview Agreement & Payment</>}
            </button>
          ) : preview.ok === false ? (
            <div className="text-red-300 text-sm" data-testid="deal-preview-error">{preview.error || `Missing: ${(preview.missing_fields || []).join(", ")}`}</div>
          ) : (
            <div className="border border-cyan-400/20 rounded-2xl bg-cyan-400/[0.03] p-5" data-testid="deal-confirm">
              <div className="mono-label text-cyan-400 text-xs mb-3">CONFIRM CLOSING</div>
              <div className="text-sm text-zinc-300 space-y-1">
                <div><b>Customer:</b> {preview.customer}</div>
                <div><b>Service:</b> {preview.service}</div>
                <div><b>Total:</b> {preview.total} {(preview.currency || "").toUpperCase()}</div>
                <div><b>Payment:</b> {preview.payment_summary}</div>
                <div><b>Sequence:</b> {preview.closing_sequence}</div>
                <div><b>Documents:</b> {preview.documents.map((x) => `${x.title}${x.requires_signature ? " (sign)" : ""}`).join(", ")}</div>
              </div>
              <button data-testid="deal-confirm-create" onClick={confirm} disabled={busy}
                className="mt-5 inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-full mono-label hover:bg-cyan-400 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> Create Agreement & Payment</>}
              </button>
            </div>
          )}
        </div>
      )}

      {d.signatures && d.signatures.length > 0 && (
        <div className="mt-8"><div className="mono-label text-zinc-600 text-[10px] mb-2">SIGNATURES</div>
          {d.signatures.map((s) => (
            <div key={s.document_type} className="text-sm text-zinc-300 flex items-center justify-between border-b border-[#141414] py-2">
              <span>{s.title} v{s.version}</span>
              <span className={`mono-label text-[10px] ${s.status === "SIGNED" ? "text-emerald-300" : "text-amber-300"}`}>{s.status}{s.executed_pdf_hash ? " ✓" : ""}</span>
            </div>
          ))}
        </div>
      )}
      {d.certificate && (
        <a data-testid="deal-certificate" href={`#`} onClick={(e) => e.preventDefault()} className="mt-6 inline-block text-emerald-300 text-xs mono-label">CERTIFICATE {d.certificate.sha256_hash.slice(0, 26)}…</a>
      )}
      <div className="mt-8"><div className="mono-label text-zinc-600 text-[10px] mb-2">AUDIT TRAIL</div>
        <div className="space-y-1" data-testid="deal-audit">
          {(d.audit || []).map((ev) => (
            <div key={ev.id} className="text-zinc-500 text-[11px] flex gap-2"><span className="text-cyan-400 mono-label">{ev.event}</span><span className="text-zinc-600">{ev.at}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Phase B: generated commercial documents ---------------- */
function DealDocuments({ dealId, proposalId }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [docType, setDocType] = useState("proposal");
  const DOC_TYPES = [["proposal", "Proposal"], ["business_roadmap", "Business Roadmap"], ["product_roadmap", "Product Roadmap"], ["gtm_roadmap", "Go-To-Market Roadmap"], ["execution_roadmap", "Execution Roadmap"], ["digital_transformation_roadmap", "Digital Transformation"]];
  const gen = async () => {
    setBusy(true);
    try {
      const src = dealId ? { deal_id: dealId } : { proposal_id: proposalId };
      setDoc(await adminGenerateDoc({ ...src, document_type: docType })); toast.success("Document generated.");
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Generation failed."); } finally { setBusy(false); }
  };
  const act = async (fn, label) => { try { const r = await fn(doc.document_id); setDoc((x) => ({ ...x, state: r.state })); toast.success(label); } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); } };
  return (
    <div className="mt-6 rounded-2xl border border-[#1f1f1f] bg-[#0b0b0c] p-5" data-testid="deal-documents">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-white font-medium"><FileText className="h-4 w-4 text-cyan-300" /> Commercial document</div>
        <div className="flex items-center gap-2">
          <select data-testid="doc-type" value={docType} onChange={(e) => setDocType(e.target.value)} className={`${field} bg-[#0b0b0c] py-1.5 w-auto`}>{DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <button data-testid="doc-generate" onClick={gen} disabled={busy} className="inline-flex items-center gap-2 bg-white text-black rounded-full px-4 py-2 mono-label hover:bg-cyan-400 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}</button>
        </div>
      </div>
      {doc && (
        <div className="mt-4" data-testid="doc-record">
          <div className="mono-label text-[11px] text-zinc-400">v{doc.version} · <span className="text-cyan-300">{doc.state}</span> · analysis: {doc.analysis_provider} · writing: {doc.writing_provider} · {doc.font_family}</div>
          <div className="text-zinc-500 text-xs mt-1 break-all">PDF {doc.pdf_hash}</div>
          <div className="mt-3 flex gap-2 flex-wrap">
            <button data-testid="doc-pdf" onClick={() => adminDownloadDoc(doc.document_id, "pdf")} className="mono-label text-[11px] border border-[#232323] text-zinc-300 rounded-full px-3 py-1.5 hover:border-cyan-400/40">PDF</button>
            <button data-testid="doc-docx" onClick={() => adminDownloadDoc(doc.document_id, "docx")} className="mono-label text-[11px] border border-[#232323] text-zinc-300 rounded-full px-3 py-1.5 hover:border-cyan-400/40">DOCX</button>
            {doc.state === "GENERATED" && <button data-testid="doc-approve" onClick={() => act(adminApproveDoc, "Approved.")} className="mono-label text-[11px] border border-emerald-400/30 text-emerald-300 rounded-full px-3 py-1.5">Approve</button>}
            {doc.state === "APPROVED" && <button data-testid="doc-send" onClick={() => act(adminSendDoc, "Sent to customer.")} className="mono-label text-[11px] bg-cyan-400 text-black rounded-full px-3 py-1.5">Send</button>}
          </div>
          {doc.state === "REVIEW_REQUIRED" && <div className="mt-2 text-amber-300 text-xs">Missing information — resolve before approving.</div>}
        </div>
      )}
    </div>
  );
}

/* ---------------- Smart DPA determination ---------------- */
function DpaDetermination({ dp, frozen, disabled, onOverride }) {
  const c = frozen || dp;
  if (!c) return (
    <div className="mt-6 rounded-2xl border border-[#1f1f1f] bg-[#0b0b0c] p-5" data-testid="dpa-determination">
      <div className="mono-label text-zinc-500 text-xs mb-1">DATA PROCESSING</div>
      <div className="text-zinc-400 text-sm">Run <b>Preview</b> to evaluate whether the AIROTECH MVP DPA is required for this engagement.</div>
    </div>
  );
  const decision = c.decision;
  const review = decision === "REVIEW_REQUIRED";
  const required = decision === "DPA_REQUIRED" || c.dpa_required;
  const tone = review ? "amber" : required ? "cyan" : "zinc";
  const toneCls = { amber: "border-amber-400/30 bg-amber-400/[0.05]", cyan: "border-cyan-400/30 bg-cyan-400/[0.05]", zinc: "border-[#1f1f1f] bg-[#0b0b0c]" }[tone];
  const reasons = c.reasons || c.classification_reasons || [];
  return (
    <div className={`mt-6 rounded-2xl border p-5 ${toneCls}`} data-testid="dpa-determination">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="mono-label text-zinc-500 text-xs mb-1">DATA PROCESSING</div>
          <div className="text-white text-sm font-medium flex items-center gap-2" data-testid="dpa-decision">
            {review ? <><AlertTriangle className="h-4 w-4 text-amber-300" /> Review Required</>
              : required ? <><ShieldAlert className="h-4 w-4 text-cyan-300" /> Personal / Data Processing Detected — DPA will be included</>
              : <><Check className="h-4 w-4 text-zinc-400" /> No Data Processing Detected — DPA not required</>}
          </div>
          {(c.operator_override || c.override) && <div className="mono-label text-[10px] text-emerald-300 mt-1">OPERATOR OVERRIDE: {(c.operator_override || c.override).decision}</div>}
        </div>
        {!frozen && (
          <div className="flex items-center gap-2">
            <button data-testid="dpa-mark-required" disabled={disabled} onClick={() => onOverride("required")} className="mono-label text-[11px] border border-cyan-400/40 text-cyan-300 rounded-full px-3 py-1.5 hover:bg-cyan-400/10 disabled:opacity-50">DPA Required</button>
            <button data-testid="dpa-mark-not-required" disabled={disabled} onClick={() => onOverride("not_required")} className="mono-label text-[11px] border border-[#232323] text-zinc-300 rounded-full px-3 py-1.5 hover:border-cyan-400/40 disabled:opacity-50">Not Required</button>
            <button data-testid="dpa-mark-auto" disabled={disabled} onClick={() => onOverride("auto")} className="mono-label text-[11px] text-zinc-500 hover:text-white px-2 disabled:opacity-50">Auto</button>
          </div>
        )}
      </div>
      {review && !frozen && <div className="mt-3 text-amber-300 text-xs">Creating the agreement is blocked until you explicitly resolve this determination.</div>}
      {reasons.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="dpa-reasons">
          {reasons.map((r, i) => (
            <li key={i} className="text-zinc-400 text-xs"><span className="mono-label text-cyan-400/80">{r.rule}</span> · <span className="text-zinc-500">{r.source}</span> — {r.reason}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------------- Notification / Alert Center ---------------- */
const SEV_CLS = { success: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10", info: "text-cyan-300 border-cyan-400/20 bg-cyan-400/[0.06]", warning: "text-amber-300 border-amber-400/30 bg-amber-400/10", error: "text-red-300 border-red-400/30 bg-red-400/10" };
function AlertsCenter() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");
  const load = async (c) => { try { setData(await adminListAlerts(c || undefined)); } catch { toast.error("Could not load alerts."); setData({ alerts: [], categories: [] }); } };
  useEffect(() => { load(cat); }, [cat]);
  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="alerts-center">
      <div className="flex items-center gap-2 mono-label text-cyan-400 mb-2"><Bell className="h-4 w-4" /> NOTIFICATION CENTER</div>
      <h1 className="font-heading font-extrabold tracking-tighter text-3xl">Admin alerts.</h1>
      <p className="mt-2 text-zinc-400 text-sm">The portal is the authoritative record. Enabled categories also email the primary administrator.</p>
      <div className="mt-6 flex gap-2 flex-wrap">
        <button onClick={() => setCat("")} className={`mono-label text-[11px] rounded-full px-3 py-1.5 border ${cat === "" ? "border-cyan-400/50 text-cyan-300" : "border-[#232323] text-zinc-400"}`}>All</button>
        {(data?.categories || ["PROSPECT", "DEAL", "AGREEMENT", "SIGNATURE", "PAYMENT", "SYSTEM"]).map((c) => (
          <button key={c} data-testid={`alert-filter-${c}`} onClick={() => setCat(c)} className={`mono-label text-[11px] rounded-full px-3 py-1.5 border ${cat === c ? "border-cyan-400/50 text-cyan-300" : "border-[#232323] text-zinc-400"}`}>{c}</button>
        ))}
      </div>
      <div className="mt-6 space-y-2" data-testid="alerts-list">
        {data == null ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>
          : data.alerts.length === 0 ? <p className="text-zinc-500 text-sm">No alerts yet.</p>
          : data.alerts.map((a) => (
            <div key={a.id} className="border border-[#1f1f1f] rounded-xl bg-[#0b0b0c] px-5 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-white text-sm font-medium truncate">{a.title}</div>
                <div className="mono-label text-zinc-500 text-[11px] mt-1">{a.category} · {a.prospect || a.engagement || "—"} {a.status ? `· ${a.status}` : ""} · {new Date(a.at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`mono-label text-[10px] px-2 py-1 rounded-full border ${SEV_CLS[a.severity] || SEV_CLS.info}`}>{a.severity}</span>
                <span className={`mono-label text-[10px] px-2 py-1 rounded-full border ${a.email_sent ? "border-emerald-400/30 text-emerald-300" : "border-[#232323] text-zinc-500"}`}>{a.email_sent ? `EMAIL ${a.email_status}` : (a.email_status || "no email").toUpperCase()}</span>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

/* ---------------- Settings ---------------- */
function SettingsPanel() {
  const [s, setS] = useState(null);
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => { try { setS(await adminGetSettings()); } catch { toast.error("Could not load settings."); } };
  useEffect(() => { load(); }, []);
  const savePrefs = async (next) => {
    setBusy(true);
    try { const r = await adminUpdateNotifications(next, s.primary_alert_email); setS((x) => ({ ...x, notifications: r.notifications, primary_alert_email: r.primary_alert_email })); toast.success("Preferences saved."); }
    catch { toast.error("Could not save preferences."); } finally { setBusy(false); }
  };
  const toggle = (c) => { const next = { ...s.notifications, [c]: !s.notifications[c] }; savePrefs(next); };
  const changePass = async () => {
    if (nw.length < 8) return toast.error("New passcode must be at least 8 characters.");
    if (nw !== cf) return toast.error("New passcode and confirmation do not match.");
    setBusy(true);
    try { await adminChangePasscode(cur, nw); setAdminPass(nw); setCur(""); setNw(""); setCf(""); toast.success("Passcode updated. Future sign-ins require the new passcode."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change passcode."); } finally { setBusy(false); }
  };
  if (!s) return <div className="flex justify-center py-40"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>;
  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="settings-panel">
      <div className="flex items-center gap-2 mono-label text-cyan-400 mb-2"><SettingsIcon className="h-4 w-4" /> SETTINGS</div>
      <h1 className="font-heading font-extrabold tracking-tighter text-3xl">Admin settings.</h1>

      <div className="mt-8 rounded-2xl border border-[#1f1f1f] bg-[#0b0b0c] p-6">
        <div className="flex items-center gap-2 text-white font-medium mb-1"><KeyRound className="h-4 w-4 text-cyan-300" /> Login passcode</div>
        <p className="text-zinc-500 text-sm mb-4">Change the internal console passcode. Your current passcode is never displayed.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label className={labelCls}>Current</Label><Input data-testid="settings-current-pass" type="password" className={field} value={cur} onChange={(e) => setCur(e.target.value)} /></div>
          <div><Label className={labelCls}>New</Label><Input data-testid="settings-new-pass" type="password" className={field} value={nw} onChange={(e) => setNw(e.target.value)} /></div>
          <div><Label className={labelCls}>Confirm new</Label><Input data-testid="settings-confirm-pass" type="password" className={field} value={cf} onChange={(e) => setCf(e.target.value)} /></div>
        </div>
        <button data-testid="settings-change-pass" onClick={changePass} disabled={busy || !cur || !nw} className="mt-4 inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full mono-label hover:bg-cyan-400 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4" /> Update passcode</>}</button>
      </div>

      <div className="mt-6 rounded-2xl border border-[#1f1f1f] bg-[#0b0b0c] p-6">
        <div className="flex items-center gap-2 text-white font-medium mb-1"><Bell className="h-4 w-4 text-cyan-300" /> Notification preferences</div>
        <p className="text-zinc-500 text-sm mb-4">Primary administrator alert email: <span className="text-cyan-300">{s.primary_alert_email}</span></p>
        <div className="grid sm:grid-cols-2 gap-2">
          {(s.alert_categories || Object.keys(s.notifications || {})).map((c) => (
            <button key={c} data-testid={`settings-toggle-${c}`} onClick={() => toggle(c)} disabled={busy}
              className={`flex items-center justify-between rounded-xl border px-4 py-3 mono-label text-xs transition-colors ${s.notifications[c] ? "border-cyan-400/40 text-cyan-300 bg-cyan-400/[0.05]" : "border-[#232323] text-zinc-500"}`}>
              <span>{c} ALERTS</span><span>{s.notifications[c] ? "ON" : "OFF"}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Services & Offerings Catalog ---------------- */
const CAT_LABELS = { BUSINESS_FORMATION: "Business Formation & Infrastructure", STRATEGY_PLANNING: "Strategy & Planning", FRACTIONAL_ONGOING: "Fractional & Ongoing Services", IMPLEMENTATION_DELIVERY: "Implementation & Delivery" };
const CX_FACTORS = { scope: 20, stakeholder: 10, workflow: 15, integration: 10, research: 10, timeline: 10, deliverable: 10, risk: 10, ongoing: 5 };
function priceLabel(s) {
  const suf = s.price_suffix ? ` ${s.price_suffix}` : "";
  if (["FIXED"].includes(s.pricing_model)) return `$${(s.base_price || 0).toLocaleString()}${suf}`;
  if (s.minimum_price != null && s.maximum_displayed_price != null) return `$${s.minimum_price.toLocaleString()}–$${s.maximum_displayed_price.toLocaleString()}${suf}`;
  return "Custom";
}
function CatalogPanel() {
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = async () => { try { setData(await adminListCatalog()); } catch { toast.error("Could not load catalog."); } };
  useEffect(() => { load(); }, []);
  const toggle = async (id) => { try { await adminToggleService(id); load(); } catch { toast.error("Toggle failed."); } };
  if (!data) return <div className="flex justify-center py-40"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>;
  const byCat = (c) => data.services.filter((s) => s.category === c);
  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="catalog-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mono-label text-cyan-400 mb-2"><Package className="h-4 w-4" /> SERVICES & OFFERINGS</div>
          <h1 className="font-heading font-extrabold tracking-tighter text-3xl">Commercial catalog.</h1>
          <p className="mt-2 text-zinc-400 text-sm">Prices are AIROTECH service fees. Government / third-party fees are always shown separately.</p>
        </div>
        <button data-testid="catalog-new-btn" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-white text-black rounded-full px-4 py-2 mono-label hover:bg-cyan-400"><Plus className="h-4 w-4" /> New Service</button>
      </div>

      <PricingIntelligence services={data.services} />
      <UpsellDetector />

      {creating && <ServiceForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}

      {CATEGORIES_ORDER.map((c) => (
        <div key={c} className="mt-8">
          <div className="mono-label text-zinc-500 text-xs mb-3">{CAT_LABELS[c]}</div>
          <div className="space-y-2">
            {byCat(c).map((s) => (
              <div key={s.service_id} data-testid={`catalog-row-${s.service_id}`} className="border border-[#1f1f1f] rounded-xl bg-[#0b0b0c] px-5 py-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-white text-sm font-medium flex items-center gap-2">{s.service_name}
                    <span className="mono-label text-[9px] px-1.5 py-0.5 rounded border border-[#232323] text-zinc-400">{s.service_type}</span>
                    <span className="mono-label text-[9px] px-1.5 py-0.5 rounded border border-[#232323] text-zinc-500">{s.pricing_model}</span>
                    {!s.active && <span className="mono-label text-[9px] px-1.5 py-0.5 rounded border border-red-400/30 text-red-300">INACTIVE</span>}
                  </div>
                  <div className="text-cyan-300 text-sm mt-1">{priceLabel(s)}</div>
                  <div className="text-zinc-500 text-xs mt-1 line-clamp-1">{s.description}</div>
                </div>
                <button data-testid={`catalog-toggle-${s.service_id}`} onClick={() => toggle(s.service_id)} className={`mono-label text-[11px] rounded-full px-3 py-1.5 border shrink-0 ${s.active ? "border-emerald-400/30 text-emerald-300" : "border-[#232323] text-zinc-500"}`}>{s.active ? "ACTIVE" : "OFF"}</button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
const CATEGORIES_ORDER = ["BUSINESS_FORMATION", "STRATEGY_PLANNING", "FRACTIONAL_ONGOING", "IMPLEMENTATION_DELIVERY"];

function PricingIntelligence({ services }) {
  const [sid, setSid] = useState(services[0]?.service_id || "");
  const [factors, setFactors] = useState({});
  const [neg, setNeg] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setRes(await adminCatalogQuote({ service_id: sid, complexity_factors: factors, negotiated_price: neg ? Number(neg) : null })); }
    catch { toast.error("Quote failed."); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 rounded-2xl border border-[#1f1f1f] bg-[#0b0b0c] p-6" data-testid="pricing-intelligence">
      <div className="flex items-center gap-2 text-white font-medium mb-3"><Calculator className="h-4 w-4 text-cyan-300" /> Pricing intelligence</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className={labelCls}>Service</Label>
          <select data-testid="quote-service" className={`${field} bg-[#0b0b0c]`} value={sid} onChange={(e) => setSid(e.target.value)}>
            {services.map((s) => <option key={s.service_id} value={s.service_id}>{s.service_name}</option>)}
          </select>
        </div>
        <div><Label className={labelCls}>Negotiated price (optional)</Label><Input data-testid="quote-negotiated" className={field} value={neg} onChange={(e) => setNeg(e.target.value)} placeholder="e.g. 6000" /></div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
        {Object.entries(CX_FACTORS).map(([f, mx]) => (
          <div key={f}>
            <Label className="mono-label text-[9px] text-zinc-500">{f} /{mx}</Label>
            <Input data-testid={`quote-factor-${f}`} type="number" className={`${field} py-1.5`} value={factors[f] ?? ""} onChange={(e) => setFactors((x) => ({ ...x, [f]: e.target.value === "" ? 0 : Number(e.target.value) }))} />
          </div>
        ))}
      </div>
      <button data-testid="quote-run" onClick={run} disabled={busy} className="mt-4 inline-flex items-center gap-2 bg-white text-black rounded-full px-5 py-2 mono-label hover:bg-cyan-400 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Calculate price"}</button>
      {res && (
        <div className="mt-4 rounded-xl border border-cyan-400/20 bg-cyan-400/[0.04] p-4" data-testid="quote-result">
          <div className="text-white text-sm">Complexity <b>{res.complexity.complexity_score}/100</b> · <span className="text-cyan-300">{res.complexity.complexity_band}</span></div>
          <div className="text-zinc-300 text-sm mt-1">
            {res.pricing.review_required ? <span className="text-amber-300">Advanced / custom — review required</span>
              : <>Recommended <b className="text-cyan-300">${(res.pricing.recommended_price || 0).toLocaleString()}</b>{res.pricing.recommended_low != null && <span className="text-zinc-500"> (range ${res.pricing.recommended_low.toLocaleString()}–${res.pricing.recommended_high.toLocaleString()})</span>}{res.pricing.recurring_interval ? `/${res.pricing.recurring_interval}` : ""}</>}
          </div>
          <div className="text-white text-sm mt-1" data-testid="quote-commercial">Commercial price: <b>{res.commercial_price != null ? `$${Number(res.commercial_price).toLocaleString()}` : "—"}</b></div>
          {(res.government_fees_separate || res.third_party_fees_separate) && <div className="mono-label text-[10px] text-zinc-400 mt-1">{res.price_suffix}</div>}
          <div className="mono-label text-[10px] text-zinc-500 mt-2">{res.complexity.reasoning_summary}</div>
        </div>
      )}
    </div>
  );
}

function UpsellDetector() {
  const [text, setText] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { setRes(await adminDetectUpsells(text, true)); } catch { toast.error("Detection failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-6 rounded-2xl border border-[#1f1f1f] bg-[#0b0b0c] p-6" data-testid="upsell-detector">
      <div className="flex items-center gap-2 text-white font-medium mb-1"><BellRing className="h-4 w-4 text-cyan-300" /> Auto-detect add-ons</div>
      <p className="text-zinc-500 text-sm mb-3">Paste a prospect's intake / notes. The strategy layer detects relevant add-ons — no manual tagging.</p>
      <Textarea data-testid="upsell-text" className={field} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. We need to launch our mobile app on both app stores and want ongoing maintenance..." />
      <button data-testid="upsell-run" onClick={run} disabled={busy} className="mt-3 inline-flex items-center gap-2 bg-white text-black rounded-full px-5 py-2 mono-label hover:bg-cyan-400 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Detect add-ons"}</button>
      {res && (
        <div className="mt-4" data-testid="upsell-result">
          {res.recommended_upsells.length === 0 ? <p className="text-zinc-500 text-sm">No relevant add-ons detected.</p>
            : <div className="space-y-2">{res.recommended_upsells.map((u) => (
                <div key={u.service_id} data-testid={`upsell-rec-${u.service_id}`} className="rounded-xl border border-cyan-400/20 bg-cyan-400/[0.04] px-4 py-3">
                  <div className="text-white text-sm font-medium">{u.service?.service_name || u.service_id}</div>
                  <div className="text-zinc-400 text-xs mt-0.5">{u.reason}</div>
                  {u.service && <div className="text-cyan-300 text-xs mt-1">{priceLabel(u.service)}</div>}
                </div>))}</div>}
          <div className="mono-label text-[10px] text-zinc-500 mt-2">{res.llm_used ? "Strategy analysis: OpenAI + deterministic rules" : "Deterministic rules"}</div>
        </div>
      )}
    </div>
  );
}

function ServiceForm({ onClose, onSaved }) {
  const [f, setF] = useState({ service_id: "", service_name: "", category: "STRATEGY_PLANNING", service_type: "CORE", pricing_model: "FIXED", base_price: "", minimum_price: "", maximum_displayed_price: "", price_suffix: "", description: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    if (!f.service_id || !f.service_name) return toast.error("service_id and name are required.");
    setBusy(true);
    try {
      await adminCreateService({ ...f, base_price: f.base_price ? Number(f.base_price) : null, minimum_price: f.minimum_price ? Number(f.minimum_price) : null, maximum_displayed_price: f.maximum_displayed_price ? Number(f.maximum_displayed_price) : null });
      toast.success("Service created."); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed."); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 rounded-2xl border border-cyan-400/20 bg-[#0b0b0c] p-6" data-testid="service-form">
      <div className="text-white font-medium mb-3">New service</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className={labelCls}>Service ID</Label><Input data-testid="svc-id" className={field} value={f.service_id} onChange={set("service_id")} /></div>
        <div><Label className={labelCls}>Name</Label><Input data-testid="svc-name" className={field} value={f.service_name} onChange={set("service_name")} /></div>
        <div><Label className={labelCls}>Category</Label><select className={`${field} bg-[#0b0b0c]`} value={f.category} onChange={set("category")}>{CATEGORIES_ORDER.map((c) => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}</select></div>
        <div><Label className={labelCls}>Type</Label><select className={`${field} bg-[#0b0b0c]`} value={f.service_type} onChange={set("service_type")}>{["CORE", "ADD_ON", "RECURRING", "BUNDLE"].map((t) => <option key={t}>{t}</option>)}</select></div>
        <div><Label className={labelCls}>Pricing model</Label><select className={`${field} bg-[#0b0b0c]`} value={f.pricing_model} onChange={set("pricing_model")}>{["FIXED", "RANGE", "RECURRING", "CUSTOM", "MILESTONE", "NEGOTIATED"].map((t) => <option key={t}>{t}</option>)}</select></div>
        <div><Label className={labelCls}>Base price</Label><Input data-testid="svc-base" className={field} value={f.base_price} onChange={set("base_price")} /></div>
        <div><Label className={labelCls}>Min price</Label><Input className={field} value={f.minimum_price} onChange={set("minimum_price")} /></div>
        <div><Label className={labelCls}>Max price</Label><Input className={field} value={f.maximum_displayed_price} onChange={set("maximum_displayed_price")} /></div>
      </div>
      <div className="mt-3"><Label className={labelCls}>Description</Label><Textarea className={field} value={f.description} onChange={set("description")} /></div>
      <div className="mt-4 flex gap-2">
        <button data-testid="svc-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-white text-black rounded-full px-5 py-2 mono-label hover:bg-cyan-400 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</button>
        <button onClick={onClose} className="mono-label text-zinc-400 hover:text-white px-3">Cancel</button>
      </div>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authed, setAuthed] = useState(!!getAdminPass());
  const [tab, setTab] = useState("reviews");
  const [openReview, setOpenReview] = useState(null);
  const [openProposal, setOpenProposal] = useState(null);
  const [openProject, setOpenProject] = useState(null);
  const [openPackage, setOpenPackage] = useState(null);
  const [openDeal, setOpenDeal] = useState(null);
  const reset = () => { setOpenReview(null); setOpenProposal(null); setOpenProject(null); setOpenPackage(null); setOpenDeal(null); };
  const logout = () => { clearAdminPass(); setAuthed(false); reset(); navigate("/admin"); };

  // Deep-link support (e.g. from internal report emails):
  // /admin?tab=reviews&review=<id>, ?tab=proposals&proposal=<id>, ?tab=projects&project=<id>,
  // ?tab=deals&deal=<id>, ?tab=packages&package=<id>. Runs on mount so the target
  // record is opened as soon as the operator is authenticated (query survives the login gate).
  useEffect(() => {
    const review = searchParams.get("review");
    const proposal = searchParams.get("proposal");
    const project = searchParams.get("project");
    const deal = searchParams.get("deal");
    const pkg = searchParams.get("package");
    const t = searchParams.get("tab");
    if (review) { setTab("reviews"); setOpenReview(review); }
    else if (proposal) { setTab("proposals"); setOpenProposal(proposal); }
    else if (project) { setTab("projects"); setOpenProject(project); }
    else if (deal) { setTab("deals"); setOpenDeal(deal); }
    else if (pkg) { setTab("packages"); setOpenPackage(pkg); }
    else if (t) { setTab(t); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) return <Gate onIn={() => setAuthed(true)} />;
  return (
    <Shell tab={tab} setTab={(t) => { setTab(t); reset(); }} onLogout={logout}>
      {tab === "reviews" && (
        openReview ? <ReviewEditor aid={openReview} onBack={() => setOpenReview(null)} onLogout={() => setAuthed(false)} />
          : <ReviewList onOpen={setOpenReview} onLogout={() => setAuthed(false)} />
      )}
      {tab === "proposals" && (
        openProposal ? <ProposalEditor id={openProposal} onBack={() => setOpenProposal(null)} onLogout={() => setAuthed(false)}
            onOpenDeal={(dealId) => { setTab("deals"); setOpenReview(null); setOpenProposal(null); setOpenProject(null); setOpenPackage(null); setOpenDeal(dealId); }} />
          : <ProposalList onOpen={setOpenProposal} onLogout={() => setAuthed(false)} />
      )}
      {tab === "projects" && (
        openProject ? <ProjectEditor id={openProject} onBack={() => setOpenProject(null)} onLogout={() => setAuthed(false)} />
          : <ProjectList onOpen={setOpenProject} onLogout={() => setAuthed(false)} />
      )}
      {tab === "packages" && (
        openPackage ? <PackageDetail id={openPackage} onBack={() => setOpenPackage(null)} />
          : <PackageList onOpen={setOpenPackage} />
      )}
      {tab === "legal" && <LegalCenter />}
      {tab === "catalog" && <CatalogPanel />}
      {tab === "growth" && <GrowthCenter />}
      {tab === "alerts" && <AlertsCenter />}
      {tab === "settings" && <SettingsPanel />}
      {tab === "deals" && (
        openDeal ? <DealDetail id={openDeal} onBack={() => setOpenDeal(null)} />
          : <DealList onOpen={setOpenDeal} />
      )}
    </Shell>
  );
}
